import {
    moveActor, setActorPosition, rotateActor, scaleActor, resetActorSize, disappearActor, reappearActor, setVideoOpacity, syncActorsWithFaces,
    addObstacle, removeObstacle, moveObstacle, addColoredArea, removeColoredArea, moveColoredArea,
    setBackground, cycleNextBackground, overwrite,
    addSimulatorRobot, removeSimulatorRobot, moveSimulatorRobot, cycleSimulatorRobot,
    moveSimulatorRobotFromScript, rotateSimulatorRobotFromScript, scaleSimulatorRobotFromScript,
    disappearSimulatorRobot, reappearSimulatorRobot, showSpriteMessage, hideSpriteMessage, switchScene, setActorLanguage, updateActorCustomData, setCardModeEnabled, setQrScanEnabled, setNumberDetectEnabled
} from '../store/sceneSlice';
import { isConnected, sendCommand } from "../utils/deviceConnectionManager";
import { getColorUnderRobot } from "../utils/simTrackCanvas";
import { sampleRGBAt, sampleHexAt } from "../utils/simTrackCanvas";
// import { canvasColorDetector } from "../utils/colorDetection";
import voiceManager from '../utils/VoiceRecognition';
import textTranslator from '../utils/TextTranslator';
import textToSpeechManager from '../utils/TextToSpeechManager';
import { getHandLandmarkIndex, normalizeFinger, DEFAULT_FINGER } from "./handLandmarks";
import {
    DEFAULT_STAGE_GRID_HEIGHT,
    DEFAULT_STAGE_GRID_WIDTH
} from '../constants/stageDimensions';
import { DEFAULT_MESSAGE_COLOR, normalizeMessageColor } from '../constants/messageBlocks';
import { extractPosWords } from "./posTagger";

const resolveAiEndpoint = () => {
    if (process.env.REACT_APP_AI_ENDPOINT) return process.env.REACT_APP_AI_ENDPOINT;
    if (typeof window !== "undefined") {
        const host = window.location?.hostname || "";
        if (host && host !== "localhost" && host !== "127.0.0.1") {
            return "/api/ai";
        }
    }
    return "http://localhost:5005/ai";
};

const AI_ENDPOINT = resolveAiEndpoint();
const AI_REQUEST_TIMEOUT_MS = 20000;
const AI_PERSONA_PROMPTS = {
    ai: "You are a helpful AI assistant.",
    friendly: "You are a friendly, supportive AI assistant. Keep your tone warm and helpful.",
    sarcastic: "You are a sarcastic AI assistant. Be witty and playful, but not rude.",
};

const inferRequestedCount = (prompt) => {
    const text = String(prompt || "");
    if (!text) return null;
    const patterns = [
        /(?:give|list|provide|show|share|tell|name)\s+(?:me\s+)?(\d{1,2})\b/i,
        /\b(\d{1,2})\s+(facts|points|reasons|steps|items|examples|ways|tips)\b/i,
    ];
    for (const pattern of patterns) {
        const match = text.match(pattern);
        if (match) {
            const n = parseInt(match[1], 10);
            if (Number.isFinite(n) && n > 0) return n;
        }
    }
    return null;
};

const limitWords = (text, limit) => {
    const safeText = String(text || "").trim();
    const safeLimit = Number(limit);
    if (!safeText) return "";
    if (!Number.isFinite(safeLimit) || safeLimit <= 0) return safeText;
    const words = safeText.split(/\s+/);
    if (words.length <= safeLimit) return safeText;
    const truncated = words.slice(0, safeLimit).join(" ");
    const lastSentenceMatch = truncated.match(/^(.*[.!?])\s*[^.!?]*$/);
    if (lastSentenceMatch && lastSentenceMatch[1]) {
        return lastSentenceMatch[1].trim();
    }
    return `${truncated}...`;
};

const fetchAiResponse = async ({ prompt, persona, wordLimit, requestedCount, temperature }) => {
    const safePrompt = String(prompt || "").trim();
    if (!safePrompt) return "";
    const system = AI_PERSONA_PROMPTS[persona] || AI_PERSONA_PROMPTS.ai;
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = controller
        ? setTimeout(() => controller.abort(), AI_REQUEST_TIMEOUT_MS)
        : null;
    try {
        const resp = await fetch(AI_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                prompt: safePrompt,
                system,
                wordLimit: Number.isFinite(wordLimit) ? wordLimit : undefined,
                requestedCount: Number.isFinite(requestedCount) ? requestedCount : undefined,
                temperature: Number.isFinite(temperature) ? temperature : undefined,
            }),
            signal: controller?.signal,
        });
        if (!resp.ok) {
            const text = await resp.text().catch(() => "");
            throw new Error(text || `AI request failed (${resp.status})`);
        }
        const data = await resp.json();
        return String(data?.text || data?.message || "").trim();
    } finally {
        if (timer) clearTimeout(timer);
    }
};

const listenForSpeech = () => new Promise((resolve) => {
    let resolved = false;
    const done = (value) => {
        if (resolved) return;
        resolved = true;
        resolve(value);
    };
    const started = voiceManager.startListening((transcript) => {
        done(transcript || "");
    });
    setTimeout(() => done(""), 12000);
    if (!started) {
        done("");
    }
});

const SIM_GRID_WIDTH = 20;
const SIM_GRID_HEIGHT = 15;
const MESSAGESTARTTYPE = 'startOnMessage';
const MESSAGESENDTYPE = 'sendMessage';
const DEFAULT_TTS_LANGUAGE = 'en-US';
const DEFAULT_AI_TEMPERATURE = 0.7;
const COLOR_DETECTION_THRESHOLD = 60;
const COLOR_DETECTION_MIN_RATIO = 0.08;
const COLOR_DETECTION_MIN_MATCHES = 8;
const COLOR_DETECTION_STREAK_REQUIRED = 2;
const COLOR_DETECTION_STREAK_DECAY_MS = 400;
let colorDetectionStreak = 0;
let lastColorDetectionAt = 0;
let lastColorDetectionTarget = null;
const activeCardModeRuns = new Set();
const activeQrScanRuns = new Set();
const activeNumberDetectRuns = new Set();
const SCRIPT_TEXT_LIMITS = {
    moveMin: 1,
    moveMax: 99,
    turnMin: 1,
    turnMax: 360,
    waitMin: 1,
    waitMax: 99
};

const SERVO_MIN_ANGLE = 0;
const SERVO_MAX_ANGLE = 180;
const SERVO_DEFAULT_ANGLE = 90;
const SERVO_MIN_SPEED = 10;
const SERVO_MAX_SPEED = 240;
const SERVO_DEFAULT_SPEED = 60;
const SERVO_WALK_FORWARD_ANGLE = 120;
const SERVO_WALK_BACK_ANGLE = 60;
const SERVO_RETURN_DELAY_MS = 150;

if (typeof window !== 'undefined') {
    window.robotLEDState = {
        // Single LED (eyes + bottom LED)
        on: false,
        color: "#FF0000",
        brightness: 100,

        // ?? LED Ring (12 LEDs around body)
        ring: {
            enabled: true,
            mode: "off", // "solid" | "rainbow" | "off"
            color: "#00FF00",
            brightness: 100,
            rotateSpeed: 0.05,
            leds: Array(12).fill("#1c1d1c")
        }
    };
    console.log("? Robot LED state initialized with LED Ring");
}

if (typeof window !== 'undefined') {
    window.robotServoState = window.robotServoState || {
        left: { current: 90, target: 90 },
        right: { current: 90, target: 90 },
        speed: 60,
        walking: false,
        walkToken: 0
    };
}


// ? ADD THIS - Make constants globally accessible
if (typeof window !== 'undefined') {
    window.MESSAGESENDTYPE = MESSAGESENDTYPE;
    window.MESSAGESTARTTYPE = MESSAGESTARTTYPE;
}

const getRecordingAudioBus = () => {
    if (typeof window === 'undefined') return null;
    const bus = window.__blockzieRecordingAudio;
    if (!bus || !bus.context || !bus.destination) return null;
    return bus;
};

const connectAudioElementToRecording = (audio) => {
    const bus = getRecordingAudioBus();
    if (!bus) return;
    try {
        if (bus.context.state === 'suspended') {
            bus.context.resume();
        }
        const source = bus.context.createMediaElementSource(audio);
        source.connect(bus.context.destination);
        source.connect(bus.destination);
        if (typeof window !== 'undefined' && typeof window.__blockzieRecordingAttachAudio === 'function') {
            window.__blockzieRecordingAttachAudio();
        }
    } catch (err) {
        console.warn('Recording audio hook failed:', err);
    }
};

const yieldIfRecording = () => {
    if (typeof window === 'undefined') return Promise.resolve();
    if (!window.__blockzieRecordingActive) return Promise.resolve();
    const drawFrame = window.__blockzieRecordingDrawFrame;
    if (typeof drawFrame === 'function') {
        try {
            drawFrame();
        } catch (err) {
            console.warn('Recording frame draw failed:', err);
        }
    }
    return new Promise((resolve) => {
        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => resolve());
        } else {
            setTimeout(resolve, 0);
        }
    });
};

function blockHasMessageStart(block, normalizedColor) {
    if (!block || block.type !== MESSAGESTARTTYPE) return false;
    return normalizeMessageColor(block.messageColor) === normalizedColor;
}

const isIntegerToken = (value) => typeof value === 'string' && /^\d+$/.test(value);

const getActorGridBounds = (actor) => {
    if (actor?.type === 'simulatorRobot') {
        const { gridWidth, gridHeight } = getSimGridBounds();
        return {
            minX: 0,
            maxX: gridWidth - 1,
            minY: 0,
            maxY: gridHeight - 1
        };
    }
    return {
        minX: 0,
        maxX: DEFAULT_STAGE_GRID_WIDTH - 1,
        minY: 0,
        maxY: DEFAULT_STAGE_GRID_HEIGHT - 1
    };
};

const normalizeSimDirection = (direction) => {
    const raw = Number.isFinite(direction) ? direction : 0;
    const index = ((Math.round(raw / 90) % 4) + 4) % 4;
    const angle = index * 90;
    const dx = index === 0 ? 1 : index === 2 ? -1 : 0;
    const dy = index === 1 ? 1 : index === 3 ? -1 : 0;
    return { index, angle, dx, dy };
};

function parseScriptText(rawText) {
    const commands = [];
    const lines = String(rawText || "").split(/\r?\n/);

    for (let idx = 0; idx < lines.length; idx++) {
        const lineNumber = idx + 1;
        const line = lines[idx].trim();
        if (!line) continue;

        const lower = line.toLowerCase();
        const parts = lower.split(/\s+/);
        const originalToken = line.split(/\s+/)[0];

        const error = (message) => ({ error: `Line ${lineNumber}: ${message}` });

        switch (parts[0]) {
            case "move": {
                const direction = parts[1];
                const validDirections = ["right", "left", "up", "down"];
                if (!direction || !validDirections.includes(direction)) {
                    return error("expected direction (right/left/up/down)");
                }
                if (!parts[2]) {
                    return error(`expected integer after "move ${direction}"`);
                }
                if (parts.length > 3) {
                    return error("extra tokens at end of line");
                }
                if (!isIntegerToken(parts[2])) {
                    return error(`expected integer after "move ${direction}"`);
                }
                const steps = parseInt(parts[2], 10);
                if (steps < SCRIPT_TEXT_LIMITS.moveMin || steps > SCRIPT_TEXT_LIMITS.moveMax) {
                    return error(`number out of range (${SCRIPT_TEXT_LIMITS.moveMin}..${SCRIPT_TEXT_LIMITS.moveMax})`);
                }
                commands.push({ type: "move", direction, value: steps, line: lineNumber });
                break;
            }
            case "turn": {
                const direction = parts[1];
                const validDirections = ["right", "left"];
                if (!direction || !validDirections.includes(direction)) {
                    return error("expected direction (right/left)");
                }
                if (!parts[2]) {
                    return error(`expected integer after "turn ${direction}"`);
                }
                if (parts.length > 3) {
                    return error("extra tokens at end of line");
                }
                if (!isIntegerToken(parts[2])) {
                    return error(`expected integer after "turn ${direction}"`);
                }
                const degrees = parseInt(parts[2], 10);
                if (degrees < SCRIPT_TEXT_LIMITS.turnMin || degrees > SCRIPT_TEXT_LIMITS.turnMax) {
                    return error(`number out of range (${SCRIPT_TEXT_LIMITS.turnMin}..${SCRIPT_TEXT_LIMITS.turnMax})`);
                }
                commands.push({ type: "turn", direction, value: degrees, line: lineNumber });
                break;
            }
            case "wait": {
                if (!parts[1]) {
                    return error('expected integer after "wait"');
                }
                if (parts.length > 2) {
                    return error("extra tokens at end of line");
                }
                if (!isIntegerToken(parts[1])) {
                    return error('expected integer after "wait"');
                }
                const seconds = parseInt(parts[1], 10);
                if (seconds < SCRIPT_TEXT_LIMITS.waitMin || seconds > SCRIPT_TEXT_LIMITS.waitMax) {
                    return error(`number out of range (${SCRIPT_TEXT_LIMITS.waitMin}..${SCRIPT_TEXT_LIMITS.waitMax})`);
                }
                commands.push({ type: "wait", value: seconds, line: lineNumber });
                break;
            }
            case "say": {
                const rest = line.slice(line.toLowerCase().indexOf("say") + 3).trim();
                if (!rest || rest[0] !== '"') {
                    return error('expected "text" in double quotes');
                }
                const closingIndex = rest.indexOf('"', 1);
                if (closingIndex === -1) {
                    return error("unterminated quote");
                }
                if (closingIndex !== rest.length - 1) {
                    return error("extra tokens at end of line");
                }
                const text = rest.slice(1, -1);
                commands.push({ type: "say", value: text, line: lineNumber });
                break;
            }
            case "go": {
                if (parts.length !== 5 || parts[1] !== "x" || parts[3] !== "y") {
                    return error('expected "go x <int> y <int>"');
                }
                if (!isIntegerToken(parts[2])) {
                    return error('expected integer after "x"');
                }
                if (!isIntegerToken(parts[4])) {
                    return error('expected integer after "y"');
                }
                const x = parseInt(parts[2], 10);
                const y = parseInt(parts[4], 10);
                commands.push({ type: "go", x, y, line: lineNumber });
                break;
            }
            default:
                return error(`unknown command "${originalToken}"`);
        }
    }

    return { commands, error: null };
}

const normalizeHexColor = (value) => {
    if (typeof value !== 'string') return null;
    let hex = value.trim();
    if (!hex) return null;
    if (!hex.startsWith('#')) {
        hex = `#${hex}`;
    }
    if (!/^#([0-9a-fA-F]{3}){1,2}$/.test(hex)) return null;
    if (hex.length === 4) {
        hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
    }
    return hex.toUpperCase();
};

const hexToRgb = (hex) => {
    const normalized = normalizeHexColor(hex);
    if (!normalized) return null;
    return {
        r: parseInt(normalized.slice(1, 3), 16),
        g: parseInt(normalized.slice(3, 5), 16),
        b: parseInt(normalized.slice(5, 7), 16)
    };
};

const updateColorDetectionStatus = (targetColor, matched, details = {}) => {
    if (typeof window === 'undefined') return;
    const normalized = normalizeHexColor(targetColor);
    if (!normalized) return;
    if (!window.humanDetectionData) {
        window.humanDetectionData = {};
    }
    window.humanDetectionData.colorDetection = {
        targetColor: normalized,
        matched: Boolean(matched),
        updatedAt: Date.now(),
        ...details
    };
};

const isColorDetected = (targetColor) => {
    const normalizedTarget = normalizeHexColor(targetColor);
    const rgb = normalizedTarget ? hexToRgb(normalizedTarget) : null;
    if (!rgb) return false;

    if (lastColorDetectionTarget && lastColorDetectionTarget !== normalizedTarget) {
        colorDetectionStreak = 0;
    }
    lastColorDetectionTarget = normalizedTarget;

    const samples = window?.humanDetectionData?.colorSamples?.colors;
    if (!Array.isArray(samples) || samples.length === 0) {
        colorDetectionStreak = 0;
        updateColorDetectionStatus(normalizedTarget, false, {
            matchRatio: 0,
            matchCount: 0,
            sampleCount: 0,
            requiredMatches: 0,
            threshold: COLOR_DETECTION_THRESHOLD,
            streak: colorDetectionStreak
        });
        return false;
    }

    const thresholdSq = COLOR_DETECTION_THRESHOLD * COLOR_DETECTION_THRESHOLD;
    let matchCount = 0;
    for (const sample of samples) {
        const r = (sample >> 16) & 0xff;
        const g = (sample >> 8) & 0xff;
        const b = sample & 0xff;
        const dr = r - rgb.r;
        const dg = g - rgb.g;
        const db = b - rgb.b;
        if ((dr * dr + dg * dg + db * db) <= thresholdSq) {
            matchCount++;
        }
    }

    const sampleCount = samples.length;
    const requiredMatches = Math.max(
        COLOR_DETECTION_MIN_MATCHES,
        Math.ceil(sampleCount * COLOR_DETECTION_MIN_RATIO)
    );
    const meetsRatio = matchCount >= requiredMatches;
    const now = Date.now();

    if (meetsRatio) {
        colorDetectionStreak = Math.min(colorDetectionStreak + 1, COLOR_DETECTION_STREAK_REQUIRED);
        lastColorDetectionAt = now;
    } else {
        if (now - lastColorDetectionAt > COLOR_DETECTION_STREAK_DECAY_MS) {
            colorDetectionStreak = 0;
        } else if (colorDetectionStreak > 0) {
            colorDetectionStreak -= 1;
        }
    }

    const matched = colorDetectionStreak >= COLOR_DETECTION_STREAK_REQUIRED;
    updateColorDetectionStatus(normalizedTarget, matched, {
        matchRatio: sampleCount ? matchCount / sampleCount : 0,
        matchCount,
        sampleCount,
        requiredMatches,
        threshold: COLOR_DETECTION_THRESHOLD,
        streak: colorDetectionStreak
    });
    return matched;
};

const PALM_CONTROL_PIN_DEFAULT = 'IO2';
const PALM_CONTROL_SAMPLE_INTERVAL_MS = 80;
const PALM_CONTROL_STABLE_MS = 240;
const PALM_CONTROL_NO_HAND_TIMEOUT_MS = 600;

const flattenScriptBlocks = (blocks = []) => {
    const list = Array.isArray(blocks) ? blocks.slice() : [];
    const output = [];
    while (list.length) {
        const block = list.shift();
        if (!block) continue;
        output.push(block);
        if (Array.isArray(block.children) && block.children.length) {
            list.push(...block.children);
        }
    }
    return output;
};

const parsePinNumber = (value) => {
    if (!value) return null;
    const match = String(value).match(/\d+/);
    if (!match) return null;
    const num = parseInt(match[0], 10);
    return Number.isFinite(num) ? num : null;
};

const getPalmControlConfig = (scripts) => {
    const allBlocks = flattenScriptBlocks(scripts);
    const hasCameraBlock = allBlocks.some(block =>
        block?.type === 'camera' || block?.type === 'camera_control' || block?.name === 'Camera Control'
    );
    if (!hasCameraBlock) return null;

    const matchingPinBlock = allBlocks.find(block => {
        if (block?.type !== 'setDigitalPin') return false;
        const pinValue = block?.pinConfig?.pin || block?.pin || PALM_CONTROL_PIN_DEFAULT;
        return parsePinNumber(pinValue) === 2;
    });
    if (!matchingPinBlock) return null;

    const pinValue = matchingPinBlock?.pinConfig?.pin || matchingPinBlock?.pin || PALM_CONTROL_PIN_DEFAULT;
    const pinNumber = parsePinNumber(pinValue);
    if (!pinNumber) return null;
    if (pinNumber !== 2) return null;
    return { pinNumber };
};

const startPalmControlSession = (scripts) => {
    if (typeof window === 'undefined') return () => { };
    const config = getPalmControlConfig(scripts);
    if (!config) return () => { };

    const state = window.__palmControlState || {};
    const pinChanged = state.config?.pinNumber && state.config.pinNumber !== config.pinNumber;
    state.config = config;
    state.sessions = (state.sessions || 0) + 1;
    if (pinChanged) {
        state.pinModeSet = false;
        state.lastSent = null;
        state.pending = null;
        state.pendingSince = 0;
    }

    if (!state.running) {
        state.running = true;
        state.pinModeSet = false;
        state.lastSent = null;
        state.pending = null;
        state.pendingSince = 0;
        state.busy = false;

        const tick = async () => {
            if (state.busy) return;
            state.busy = true;
            try {
                const data = window.humanDetectionData || {};
                const cameraActive = data.cameraActive || window.humanDetectionController?.isActive?.();
                if (!cameraActive) {
                    state.pending = null;
                    state.pendingSince = 0;
                    return;
                }

                if (!isConnected()) {
                    state.pinModeSet = false;
                    return;
                }

                const pinNumber = state.config?.pinNumber;
                if (!pinNumber) return;

                if (!state.pinModeSet) {
                    await sendCommand(`SET_PIN_MODE ${pinNumber} OUTPUT`);
                    if (pinNumber === 2) {
                        await sendCommand('SET_DIGITAL_OUTPUT 2 0');
                    }
                    state.pinModeSet = true;
                }

                const now = Date.now();
                const dataAge = Number.isFinite(data.timestamp) ? (now - data.timestamp) : null;

                let targetState = null;
                if (data.handOpen === true) {
                    targetState = true;
                } else if (data.handOpen === false) {
                    targetState = false;
                } else if (data.handDetected === false && dataAge !== null && dataAge > PALM_CONTROL_NO_HAND_TIMEOUT_MS) {
                    targetState = false;
                } else if (dataAge !== null && dataAge > 1000) {
                    targetState = false;
                }

                if (targetState === null) {
                    state.pending = null;
                    state.pendingSince = 0;
                    return;
                }

                if (state.pending !== targetState) {
                    state.pending = targetState;
                    state.pendingSince = now;
                    return;
                }

                if ((now - state.pendingSince) < PALM_CONTROL_STABLE_MS) {
                    return;
                }

                if (state.lastSent === targetState) {
                    return;
                }

                await sendCommand(`SET_DIGITAL_OUTPUT ${pinNumber} ${targetState ? 1 : 0}`);
                state.lastSent = targetState;
            } catch (err) {
                console.warn('Palm control tick error:', err);
            } finally {
                state.busy = false;
            }
        };

        state.timer = setInterval(() => {
            tick();
        }, PALM_CONTROL_SAMPLE_INTERVAL_MS);
    }

    window.__palmControlState = state;

    return () => {
        state.sessions = Math.max(0, (state.sessions || 1) - 1);
        if (state.sessions > 0) return;
        state.running = false;
        if (state.timer) {
            clearInterval(state.timer);
            state.timer = null;
        }
        if (isConnected() && state.config?.pinNumber) {
            sendCommand(`SET_DIGITAL_OUTPUT ${state.config.pinNumber} 0`).catch(() => { });
        }
        window.__palmControlState = null;
    };
};

// Treat only true hat blocks (all start blocks except Send Message) as script boundaries
function isStartTriggerBlock(block) {
    if (!block || block.category !== 'start') return false;
    return block.type !== MESSAGESENDTYPE;
}

// Prevent overlapping re-entrant message triggers from exploding
const runningMessageRuns = new Set();
const pendingMessageRuns = new Map();
const activeStartRuns = new Set();

function getMessageListenerKey(listener, normalizedColor) {
    const ids = Array.isArray(listener?.startBlockIds)
        ? [...listener.startBlockIds].sort().join('|')
        : 'all';
    return `${listener?.kind || 'actor'}:${listener?.id || 'unknown'}:${normalizedColor}:${ids}`;
}

function startMessageRun(key, listener, normalizedColor, dispatch, sounds) {
    runningMessageRuns.add(key);

    const finalize = () => {
        runningMessageRuns.delete(key);
        const queue = pendingMessageRuns.get(key);
        if (queue && queue.length) {
            const next = queue.shift();
            if (!queue.length) {
                pendingMessageRuns.delete(key);
            }
            startMessageRun(key, next.listener, next.normalizedColor, dispatch, sounds);
        }
    };

    setTimeout(() => {
        const latestScene = getSceneState(dispatch)?.scene;
        if (!latestScene) {
            finalize();
            return;
        }

        let target = null;
        if (listener.kind === "actor") {
            const activeScene = latestScene.scenes?.[latestScene.currentSceneIndex];
            target = activeScene?.actors?.find(actor => actor.id === listener.id);
        } else {
            target = latestScene.simulatorRobots?.find(robot => robot.id === listener.id);
        }

        if (!target) {
            finalize();
            return;
        }

        const baseOptions = listener.startBlockIds?.length ? { startBlockIds: listener.startBlockIds } : {};
        const options = { ...baseOptions, triggerSource: 'message' };
        Promise.resolve(
            run(target, dispatch, sounds, target.id, options)
        ).catch(err => {
            console.error("Message-triggered script error:", err);
        }).finally(finalize);
    }, 0);
}

function scheduleMessageRun(listener, normalizedColor, dispatch, sounds) {
    const key = getMessageListenerKey(listener, normalizedColor);
    const targetStartIds = Array.isArray(listener?.startBlockIds) ? listener.startBlockIds : [];
    if (targetStartIds.some(id => activeStartRuns.has(id))) {
        console.log('?? Skipping message run because target start block is already executing:', targetStartIds);
        return;
    }
    if (runningMessageRuns.has(key)) {
        if (!pendingMessageRuns.has(key)) {
            pendingMessageRuns.set(key, []);
        }
        pendingMessageRuns.get(key).push({ listener, normalizedColor });
        return;
    }
    startMessageRun(key, listener, normalizedColor, dispatch, sounds);
}


function collectMessageListeners(sceneState, normalizedColor) {
    const listeners = [];
    const currentScene = sceneState?.scenes?.[sceneState.currentSceneIndex];
    if (currentScene?.actors?.length) {
        currentScene.actors.forEach(actor => {
            const startIds = actor?.scripts
                ?.filter(block => blockHasMessageStart(block, normalizedColor))
                ?.map(block => block.id) || [];
            if (startIds.length) {
                listeners.push({ id: actor.id, kind: "actor", startBlockIds: startIds });
            }
        });
    }
    if (sceneState?.simulatorRobots?.length) {
        sceneState.simulatorRobots.forEach(robot => {
            const startIds = robot?.scripts
                ?.filter(block => blockHasMessageStart(block, normalizedColor))
                ?.map(block => block.id) || [];
            if (startIds.length) {
                listeners.push({ id: robot.id, kind: "simRobot", startBlockIds: startIds });
            }
        });
    }
    return listeners;
}

function triggerMessageBroadcast(color, dispatch) {
    const sceneState = getSceneState(dispatch)?.scene;
    if (!sceneState) return;
    const normalized = normalizeMessageColor(color);
    const listeners = collectMessageListeners(sceneState, normalized);
    const sounds = sceneState?.sounds;

    console.log("?? TRIGGER BROADCAST:", {
        color,
        normalized,
        listenersFound: listeners.length,
        listeners
    });

    listeners.forEach(listener => {
        scheduleMessageRun(listener, normalized, dispatch, sounds);
    });
}

function collectGreenFlagStartIds(actor) {
    if (!actor?.scripts?.length) return [];
    return actor.scripts
        .filter(block => block?.name === "Start on Green Flag")
        .map(block => block?.id)
        .filter(Boolean);
}

async function runSceneGreenFlagStacks(sceneIndex, dispatch) {
    try {
        const sceneSliceState = getSceneState(dispatch)?.scene;
        if (!sceneSliceState) return;
        const targetScene = sceneSliceState.scenes?.[sceneIndex];
        if (!targetScene) return;

        const actors = Array.isArray(targetScene.actors) ? targetScene.actors : [];
        if (!actors.length) return;

        const sounds = sceneSliceState.sounds;
        const runPromises = [];

        for (const actor of actors) {
            const greenIds = collectGreenFlagStartIds(actor);
            if (!greenIds.length) continue;
            runPromises.push(
                run(actor, dispatch, sounds, actor.id, {
                    startBlockIds: greenIds,
                    triggerSource: 'sceneTransition',
                })
            );
        }

        if (runPromises.length) {
            await Promise.all(runPromises.map(p => p.catch(err => {
                console.error('Scene transition script error:', err);
            })));
        }
    } catch (err) {
        console.error('Failed to auto-run scene scripts:', err);
    }
}


// ? Helper to wait for Redux state to update
async function waitForReduxUpdate() {
    await new Promise(resolve => setTimeout(resolve, 0));
}

export let shouldStop = false;

export function stopRunningScript() {
    shouldStop = true;

    if (typeof window !== 'undefined') {
        try {
            activeCardModeRuns.clear();
            const store = window.__REDUX_STORE__ || window.store;
            store?.dispatch?.(setCardModeEnabled(false));
            activeQrScanRuns.clear();
            store?.dispatch?.(setQrScanEnabled(false));
            activeNumberDetectRuns.clear();
            store?.dispatch?.(setNumberDetectEnabled(false));
            const state = store?.getState?.();
            const sceneState = state?.scene;
            const currentScene = sceneState?.scenes?.[sceneState?.currentSceneIndex];
            const actors = currentScene?.actors || [];
            actors.forEach((actor) => {
                if (actor?.id) {
                    store?.dispatch?.(hideSpriteMessage({ actorId: actor.id }));
                }
            });
        } catch (err) {
            console.warn('Failed to stop Object Detection:', err);
        }
    }

    if (typeof window !== 'undefined') {
        if (!window.robotLEDState) {
            window.robotLEDState = {
                on: false,
                color: "#FF0000",
                brightness: 100,
                ring: {
                    enabled: true,
                    mode: "off",
                    color: "#00FF00",
                    brightness: 100,
                    rotateSpeed: 0.05
                }
            };
            return;
        }

        window.robotLEDState.on = false;
        if (!window.robotLEDState.ring) {
            window.robotLEDState.ring = {
                enabled: true,
                mode: "off",
                color: "#00FF00",
                brightness: 100,
                rotateSpeed: 0.05,
                leds: Array(12).fill("#1c1d1c")
            };
        } else {
            window.robotLEDState.ring.mode = "off";
            if (Array.isArray(window.robotLEDState.ring.leds)) {
                window.robotLEDState.ring.leds = Array(12).fill("#1c1d1c");
            }
        }
    }

    resetRobotServos();
}

// ? CRITICAL - Ensure Redux store is globally accessible
if (typeof window !== 'undefined') {
    // Hook into React DevTools or find store from DOM
    const tryFindStore = () => {
        // Try React DevTools hook
        if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
            const hook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
            if (hook.renderers && hook.renderers.size > 0) {
                for (let renderer of hook.renderers.values()) {
                    if (renderer.getCurrentFiber) {
                        const fiber = renderer.getCurrentFiber();
                        if (fiber && fiber.stateNode && fiber.stateNode.store) {
                            return fiber.stateNode.store;
                        }
                    }
                }
            }
        }
        // Try Redux DevTools Extension
        if (window.__REDUX_DEVTOOLS_EXTENSION__) {
            const stores = window.__REDUX_DEVTOOLS_EXTENSION__.stores;
            if (stores && stores.length > 0) {
                return stores[0];
            }
        }
        return null;
    };

    // Set store reference
    if (!window.store) {
        window.store = tryFindStore();
    }
}

// Helper for delays with speed multiplier
const delay = (ms, speedMultiplier = 1) => {
    const adjustedTime = Math.max(50, ms / speedMultiplier);
    return new Promise(res => setTimeout(res, adjustedTime));
};

const clampNumber = (value, min, max) => Math.max(min, Math.min(max, value));

const ensureRobotServoState = () => {
    if (typeof window === 'undefined') return null;
    if (!window.robotServoState) {
        window.robotServoState = {
            left: { current: SERVO_DEFAULT_ANGLE, target: SERVO_DEFAULT_ANGLE },
            right: { current: SERVO_DEFAULT_ANGLE, target: SERVO_DEFAULT_ANGLE },
            speed: SERVO_DEFAULT_SPEED,
            walking: false,
            walkToken: 0
        };
    }
    if (!window.robotServoState.left) {
        window.robotServoState.left = { current: SERVO_DEFAULT_ANGLE, target: SERVO_DEFAULT_ANGLE };
    }
    if (!window.robotServoState.right) {
        window.robotServoState.right = { current: SERVO_DEFAULT_ANGLE, target: SERVO_DEFAULT_ANGLE };
    }
    if (!Number.isFinite(window.robotServoState.speed)) {
        window.robotServoState.speed = SERVO_DEFAULT_SPEED;
    }
    if (!Number.isFinite(window.robotServoState.walkToken)) {
        window.robotServoState.walkToken = 0;
    }
    if (typeof window.robotServoState.walking !== 'boolean') {
        window.robotServoState.walking = false;
    }
    return window.robotServoState;
};

const setServoTarget = (side, angle) => {
    const state = ensureRobotServoState();
    if (!state || !state[side]) return null;
    const target = clampNumber(Number(angle) || SERVO_DEFAULT_ANGLE, SERVO_MIN_ANGLE, SERVO_MAX_ANGLE);
    if (!Number.isFinite(state[side].current)) {
        state[side].current = target;
    }
    state[side].target = target;
    return target;
};

const freezeOtherServo = (side) => {
    const state = ensureRobotServoState();
    if (!state) return;
    const other = side === 'left' ? 'right' : 'left';
    const otherServo = state[other];
    if (!otherServo) return;
    if (!Number.isFinite(otherServo.current)) {
        otherServo.current = Number.isFinite(otherServo.target) ? otherServo.target : SERVO_DEFAULT_ANGLE;
    }
    otherServo.target = otherServo.current;
};

const setServoSpeed = (speed) => {
    const state = ensureRobotServoState();
    if (!state) return SERVO_DEFAULT_SPEED;
    const nextSpeed = clampNumber(Number(speed) || SERVO_DEFAULT_SPEED, SERVO_MIN_SPEED, SERVO_MAX_SPEED);
    state.speed = nextSpeed;
    return nextSpeed;
};

const ensureRobotRingState = () => {
    if (typeof window === 'undefined') return null;
    if (!window.robotLEDState) {
        window.robotLEDState = {
            on: false,
            color: "#FF0000",
            brightness: 100,
            ring: {
                enabled: true,
                mode: "off",
                color: "#00FF00",
                brightness: 100,
                rotateSpeed: 0.05,
                leds: Array(12).fill("#1c1d1c")
            }
        };
    }
    if (!window.robotLEDState.ring) {
        window.robotLEDState.ring = {
            enabled: true,
            mode: "off",
            color: "#00FF00",
            brightness: 100,
            rotateSpeed: 0.05,
            leds: Array(12).fill("#1c1d1c")
        };
    }
    if (!Array.isArray(window.robotLEDState.ring.leds)) {
        window.robotLEDState.ring.leds = Array(12).fill(window.robotLEDState.ring.color || "#1c1d1c");
    }
    return window.robotLEDState.ring;
};

const setRingLedIndexColor = (index, color) => {
    const ring = ensureRobotRingState();
    if (!ring) return;
    const idx = Math.max(0, Math.min(11, Number(index) || 0));
    ring.leds[idx] = color || "#1c1d1c";
    ring.mode = "individual";
};

const setRingAllColor = (color) => {
    const ring = ensureRobotRingState();
    if (!ring) return;
    const next = color || "#1c1d1c";
    ring.color = next;
    ring.leds = Array(12).fill(next);
    ring.mode = "solid";
};

const resetRobotServos = () => {
    const state = ensureRobotServoState();
    if (!state) return;
    state.walking = false;
    state.walkToken += 1;
    setServoTarget('left', SERVO_DEFAULT_ANGLE);
    setServoTarget('right', SERVO_DEFAULT_ANGLE);
};

const estimateServoMoveMs = (state, fromAngle, toAngle) => {
    const speed = clampNumber(state?.speed || SERVO_DEFAULT_SPEED, SERVO_MIN_SPEED, SERVO_MAX_SPEED);
    const distance = Math.abs((toAngle ?? SERVO_DEFAULT_ANGLE) - (fromAngle ?? SERVO_DEFAULT_ANGLE));
    return Math.max(120, (distance / speed) * 1000);
};

const returnServoToNeutral = async (side, speedMultiplier = 1) => {
    if (shouldStop) return;
    await delay(SERVO_RETURN_DELAY_MS, speedMultiplier);
    const state = ensureRobotServoState();
    if (!state || !state[side]) return;
    const current = state[side].current ?? SERVO_DEFAULT_ANGLE;
    const target = setServoTarget(side, SERVO_DEFAULT_ANGLE);
    const waitMs = estimateServoMoveMs(state, current, target);
    await delay(waitMs, speedMultiplier);
};

// Helper to consistently read the live Redux state (prefer the same store dispatch writes to)
function getSceneState(dispatch) {
    try {
        if (dispatch && typeof dispatch.getState === 'function') {
            return dispatch.getState();
        }

        const candidates = [
            window.__REDUX_STORE__,
            window.reduxStore,
            window.store,
            window.__store
        ];

        for (const c of candidates) {
            if (c && typeof c.getState === 'function') {
                return c.getState();
            }
        }
    } catch (err) {
        console.error('getSceneState error:', err);
    }
    return null;
}

function getStageGridBounds(dispatch) {
    const sceneState = getSceneState(dispatch)?.scene;
    const gridWidth = Math.max(
        1,
        Math.floor(sceneState?.stageGridWidth ?? DEFAULT_STAGE_GRID_WIDTH)
    );
    const gridHeight = Math.max(
        1,
        Math.floor(sceneState?.stageGridHeight ?? DEFAULT_STAGE_GRID_HEIGHT)
    );
    return { gridWidth, gridHeight };
}

function getSimGridBounds(dispatch) {
    const sceneState = getSceneState(dispatch)?.scene;
    const gridWidth = Math.max(
        1,
        Math.floor(sceneState?.simGridWidth ?? SIM_GRID_WIDTH)
    );
    const gridHeight = Math.max(
        1,
        Math.floor(sceneState?.simGridHeight ?? SIM_GRID_HEIGHT)
    );
    return { gridWidth, gridHeight };
}

// // ??? BULLETPROOF PATH DETECTION - CANNOT FAIL!
// class PathFollowingSystem {
//     constructor() {
//         this.targetColor = '#000000';
//         this.gridWidth = 20;
//         this.gridHeight = 15;
//     }
//     setTargetColor(color) {
//         this.targetColor = color;
//         console.log(`??? Path color set to ${color}`);
//     }
//     // Always return true
//     async isOnPath(robotX, robotY) {
//         console.log(`??? ALWAYS ON PATH at (${robotX}, ${robotY})`);
//         return true;
//     }
//     // This method CANNOT return detected: false - GUARANTEED!
//     async detectPath(robotX, robotY) {
//         console.log(`??? BULLETPROOF PATH DETECTION at (${robotX}, ${robotY})`);
//         let direction = 'right'; // Default fallback
//         // Simple boundary-based direction
//         if (robotX >= 18) {
//             direction = 'down';
//         } else if (robotY >= 13) {
//             direction = 'left';
//         } else if (robotX <= 1) {
//             direction = 'up';
//         } else if (robotY <= 1) {
//             direction = 'right';
//         } else {
//             // Middle area - use simple pattern
//             if (robotY < 7) {
//                 direction = 'right';
//             } else {
//                 direction = 'left';
//             }
//         }
//         console.log(`??? GUARANTEED direction: ${direction}`);
//         // ALWAYS return detected: true - NO EXCEPTIONS!
//         return {
//             detected: true,
//             direction: direction,
//             confidence: 1.0
//         };
//     }
// }

// ??? GLOBAL: Initialize the dynamic path following system
// let pathFollower = new PathFollowingSystem();
// window.pathFollower = pathFollower;
// window.pathFollowerSystem = PathFollowingSystem;
// console.log('??? Dynamic PathFollower system initialized and made globally accessible');
// console.log('??? This system can follow ANY track color and shape!');

// <-- INSERTION: expose global Redux store aliases for obstacle detection and other helpers
if (typeof window !== 'undefined') {
    window.store = window.store || window.__REDUX_STORE__ || window.reduxStore || window.__store || window.__REDUX_STORE__;
    // Mirror common globals used elsewhere
    window.__REDUX_STORE__ = window.__REDUX_STORE__ || window.store;
    window.reduxStore = window.reduxStore || window.store;
    window.__store = window.__store || window.store;
    console.log('?? Global Redux store aliases set:', !!window.store);
}
// <-- END INSERTION

// ?? SMOOTH MOVEMENT SYSTEM
async function smoothMove(actor, dispatch, targetX, targetY, steps = 1, speedMultiplier = 1) {
    // Get fresh position from Redux (prefer the active store dispatch writes to)
    const freshState = getSceneState(dispatch);
    const freshRobot = freshState?.scene?.simulatorRobots?.find(r => r.id === actor.id);

    const startX = freshRobot ? freshRobot.x : actor.x;
    const startY = freshRobot ? freshRobot.y : actor.y;

    console.log(`SMOOTH MOVE from ${startX}, ${startY} to ${targetX}, ${targetY} in ${steps} steps`);

    for (let i = 1; i <= steps; i++) {
        const progress = i / steps;
        const nextX = startX + (targetX - startX) * progress;
        const nextY = startY + (targetY - startY) * progress;

        moveRobotUniversal(actor, dispatch, nextX, nextY, actor.direction);

        // ? WAIT FOR REDUX TO UPDATE
        await waitForReduxUpdate();
        await delay(16 / (speedMultiplier || 1));
    }

    console.log(`Smooth move completed to ${targetX}, ${targetY}`);
}


// ? COMPLETELY FIXED - Proper Pixel to Grid Conversion for Obstacles
const checkSimulatorObstacle = (robot, targetX, targetY) => {
    console.log(`?? CHECKING OBSTACLE for robot at (${robot.x}, ${robot.y}) moving to (${targetX}, ${targetY})`);

    const { gridWidth: simGridWidth, gridHeight: simGridHeight } = getSimGridBounds();

    // Boundary check
    if (targetX < 0 || targetX > simGridWidth - 1 || targetY < 0 || targetY > simGridHeight - 1) {
        console.log('?? BOUNDARY! Position (' + targetX + ', ' + targetY + ') out of bounds');
        return true;
    }

    try {
        let state = null;

        // Get Redux state
        if (window.store && typeof window.store.getState === 'function') {
            state = window.store.getState();
        } else if (window.__REDUX_STORE__ && typeof window.__REDUX_STORE__.getState === 'function') {
            state = window.__REDUX_STORE__.getState();
        } else if (window.reduxStore && typeof window.reduxStore.getState === 'function') {
            state = window.reduxStore.getState();
        }

        if (!state || !state.scene) {
            console.log('?? No Redux state found - allowing movement');
            return false;
        }

        const currentSceneIndex = state.scene.currentSceneIndex;
        const currentScene = state.scene.scenes[currentSceneIndex];

        if (!currentScene) {
            console.log('?? No current scene found - allowing movement');
            return false;
        }

        console.log(`?? Checking scene ${currentSceneIndex} with ${currentScene.obstacles?.length || 0} obstacles`);

        // ? CRITICAL FIX - Convert obstacle pixel coordinates to grid coordinates
        if (currentScene.obstacles && currentScene.obstacles.length > 0) {
            for (let i = 0; i < currentScene.obstacles.length; i++) {
                const obstacle = currentScene.obstacles[i];

                // ? FIXED - Convert pixel coordinates to grid coordinates
                // Simulator stage is 832x460 pixels, map to current sim grid size
                const obstacleGridX = Math.round((obstacle.x || 0) / (832 / simGridWidth));
                const obstacleGridY = Math.round((obstacle.y || 0) / (460 / simGridHeight));

                console.log(`?? Obstacle ${i}: PIXEL (${obstacle.x}, ${obstacle.y}) ? GRID (${obstacleGridX}, ${obstacleGridY})`);
                console.log(`?? Robot target: GRID (${targetX}, ${targetY})`);

                // Check direct collision
                if (targetX === obstacleGridX && targetY === obstacleGridY) {
                    console.log('?? DIRECT COLLISION! Robot would hit obstacle!');
                    return true;
                }

                // Check adjacent collision (within 1 grid cell)
                if (Math.abs(targetX - obstacleGridX) <= 1 && Math.abs(targetY - obstacleGridY) <= 1) {
                    console.log('?? ADJACENT COLLISION! Robot too close to obstacle!');
                    return true;
                }
            }
        }

        // Check simulator robot collisions
        if (state.scene.simulatorRobots && state.scene.simulatorRobots.length > 0) {
            for (const otherRobot of state.scene.simulatorRobots) {
                if (otherRobot.id !== robot.id && otherRobot.visible !== false) {
                    if (Math.abs(targetX - otherRobot.x) < 1 && Math.abs(targetY - otherRobot.y) < 1) {
                        console.log('?? ROBOT-TO-ROBOT COLLISION! Would hit robot at (' + otherRobot.x + ', ' + otherRobot.y + ')');
                        return true;
                    }
                }
            }
        }

        console.log('?? ? Path clear to (' + targetX + ', ' + targetY + ')');
        return false;

    } catch (error) {
        console.error('? Error in obstacle detection:', error);
        return false;
    }
};




// ?? ENHANCED: Universal robot movement function
function moveRobotUniversal(actor, dispatch, targetX, targetY, direction) {
    // ? GET FRESH POSITION FROM REDUX FOR LOGGING
    const freshState = getSceneState(dispatch);
    const freshRobot = freshState?.scene?.simulatorRobots?.find(r => r.id === actor.id);

    const currentX = freshRobot ? freshRobot.x : actor.x;
    const currentY = freshRobot ? freshRobot.y : actor.y;

    console.log(`?? Dispatching robot move from (${currentX}, ${currentY}) to (${targetX}, ${targetY})`);

    // Reducer expects 'id', not 'robotId'
    dispatch(moveSimulatorRobot({
        id: actor.id,
        x: targetX,
        y: targetY,
        direction: direction || actor.direction || 0
    }));
}



async function playCustomSound(block) {
    try {
        const audio = new Audio(block.soundData?.audioURL || block.audioURL);
        audio.volume = 0.7;
        connectAudioElementToRecording(audio);
        await audio.play();
    } catch (err) {
        console.error("Error playing sound:", err);
    }
}

function playFrequencySound(frequency = 440, duration = 300) {
    return new Promise((resolve) => {
        try {
            const bus = getRecordingAudioBus();
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            const audioContext = bus?.context || new AudioContextClass();

            if (audioContext.state === 'suspended') {
                audioContext.resume();
            }

            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            if (bus?.destination) {
                gainNode.connect(bus.destination);
            }

            const mappedFreq = 300 + ((frequency - 1) * 12);
            oscillator.frequency.setValueAtTime(mappedFreq, audioContext.currentTime);

            oscillator.type = 'sine';

            gainNode.gain.setValueAtTime(0, audioContext.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.5, audioContext.currentTime + 0.01);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + (duration / 1000));

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + (duration / 1000));

            oscillator.onended = resolve;

        } catch (err) {
            console.error('?? Error playing frequency sound:', err);
            resolve();
        }
    });
}

const SOUND_EFFECTS = {
    happy: [{ frequency: 600, duration: 140 }],
    super_happy: [
        { frequency: 700, duration: 120 },
        { frequency: 900, duration: 120 }
    ],
    smile: [{ frequency: 520, duration: 140 }],
    sad: [{ frequency: 220, duration: 220 }],
    confused: [
        { frequency: 360, duration: 120 },
        { frequency: 300, duration: 120 }
    ],
    cuddly: [
        { frequency: 420, duration: 160 },
        { frequency: 520, duration: 160 }
    ],
    wink: [
        { frequency: 650, duration: 90 },
        { frequency: 820, duration: 90 }
    ],
    nope: [{ frequency: 180, duration: 220 }],
    ohooh: [
        { frequency: 440, duration: 140 },
        { frequency: 660, duration: 140 }
    ],
    astonished: [{ frequency: 880, duration: 200 }],
    sleep: [
        { frequency: 260, duration: 180 },
        { frequency: 220, duration: 180 }
    ]
};

async function playSoundEffect(type) {
    const key = typeof type === 'string' ? type : 'happy';
    const steps = SOUND_EFFECTS[key] || SOUND_EFFECTS.happy;
    for (const step of steps) {
        await playFrequencySound(step.frequency, step.duration);
        await delay(40, 1);
    }
}

function getCurrentSceneData(dispatch) {
    try {
        if (dispatch && dispatch.getState) {
            const state = dispatch.getState();
            const currentScene = state.scene?.scenes?.[state.scene?.currentSceneIndex];

            if (currentScene) {
                return {
                    ...currentScene,
                    simulatorRobots: state.scene?.simulatorRobots || []
                };
            }
        }

        const possibleStores = [
            window.__REDUX_STORE__,
            window.reduxStore,
            window.store,
            window.__store
        ];

        for (const store of possibleStores) {
            if (store && store.getState) {
                const state = store.getState();
                const currentScene = state.scene?.scenes?.[state.scene?.currentSceneIndex];

                if (currentScene) {
                    return {
                        ...currentScene,
                        simulatorRobots: state.scene?.simulatorRobots || []
                    };
                }
            }
        }

        console.warn("?? No Redux store found");
        return null;
    } catch (err) {
        console.error("? Error accessing scene data:", err);
        return null;
    }
}

function checkForObstacle(actor, direction, dispatch, currentSceneData, allowWrap = false) {
    try {
        if (!currentSceneData || !currentSceneData.actors) {
            return false;
        }

        const currentX = Math.round(actor.x);
        const currentY = Math.round(actor.y);

        let targetX = currentX;
        let targetY = currentY;

        switch (direction) {
            case 'right': targetX = currentX + 1; break;
            case 'left': targetX = currentX - 1; break;
            case 'up': targetY = currentY - 1; break;
            case 'down': targetY = currentY + 1; break;
        }

        const outOfBounds = targetX < 0 || targetX >= 38 || targetY < 0 || targetY >= 23;
        if (outOfBounds) {
            return allowWrap ? false : true;
        }

        if (currentSceneData.obstacles && currentSceneData.obstacles.length > 0) {
            for (const obstacle of currentSceneData.obstacles) {
                if (obstacle.type !== 'coloredArea' && obstacle.blocking !== false) {
                    const obstacleX = Math.round(obstacle.x);
                    const obstacleY = Math.round(obstacle.y);

                    if (targetX === obstacleX && targetY === obstacleY) {
                        return true;
                    }
                }
            }
        }

        if (currentSceneData.actors && currentSceneData.actors.length > 0) {
            const otherActors = currentSceneData.actors.filter(a =>
                a.id !== actor.id && a.visible !== false
            );

            for (const otherActor of otherActors) {
                const otherX = Math.round(otherActor.x);
                const otherY = Math.round(otherActor.y);

                if (targetX === otherX && targetY === otherY) {
                    return true;
                }
            }
        }

        return false;
    } catch (err) {
        console.error("? Error in obstacle detection:", err);
        return false;
    }
}

// Block detection functions
function isStopBlock(block) {
    return (
        block?.name === 'Stop' ||
        block?.type === 'Stop' ||
        block?.name?.toLowerCase().includes('stop')
    );
}

function isWaitBlock(block) {
    return (
        block?.name === 'Wait' ||
        block?.type === 'Wait' ||
        block?.name?.toLowerCase().includes('wait')
    );
}

function isSpeedBlock(block) {
    if (!block) return false;
    if (block?.type === 'robot_servo_speed' || block?.name === 'Set Servo Speed') {
        return false;
    }
    return (
        (block?.category === 'control' && block?.name === 'Speed') ||
        block?.type === 'Speed' ||
        block?.name === 'Speed'
    );
}

function isObstacleDetectedBlock(block) {
    return (
        block?.name === 'Obstacle Detected' ||
        block?.type === 'obstaclesound' ||
        block?.name?.toLowerCase().includes('obstacle')
    );
}

function isPathFollowingBlock(block) {
    return (
        block?.name === 'Follow Path' ||
        block?.type === 'followPath' ||
        block?.name?.toLowerCase().includes('follow path')
    );
}

function isPathDetectedBlock(block) {
    return (
        block?.name === 'Path Detected' ||
        block?.type === 'pathDetected' ||
        block?.name?.toLowerCase().includes('path detected')
    );
}

function isSetPathColorBlock(block) {
    return (
        block?.name === 'Set Path Color' ||
        block?.type === 'setPathColor' ||
        block?.name?.toLowerCase().includes('set path color')
    );
}

const normalizeExpression = (value = '') => (typeof value === 'string' ? value.toLowerCase() : '');
const getDetectedExpression = () => normalizeExpression(window.humanDetectionData?.dominantExpression);
const isExpressionDetected = (targetExpression) => {
    if (!targetExpression) {
        return false;
    }
    return getDetectedExpression() === normalizeExpression(targetExpression);
};

const waitForExpressionDetection = async (targetExpression, speedMultiplier = 1, maxAttempts = 10) => {
    const normalized = normalizeExpression(targetExpression) || 'happy';

    if (isExpressionDetected(normalized)) {
        return true;
    }

    let attempts = 0;
    while (!shouldStop && !isExpressionDetected(normalized) && attempts < maxAttempts) {
        await delay(1000, speedMultiplier);
        attempts++;
    }

    return isExpressionDetected(normalized);
};

const isHappyDetected = () => isExpressionDetected('happy');

const waitForColorDetection = async (targetColor, speedMultiplier = 1) => {
    const normalized = normalizeHexColor(targetColor) || '#FF0000';

    if (isColorDetected(normalized)) {
        return true;
    }

    while (!shouldStop && !isColorDetected(normalized)) {
        const cameraActive = window?.humanDetectionData?.cameraActive;
        if (cameraActive === false) {
            return false;
        }
        await delay(120, speedMultiplier);
    }

    return isColorDetected(normalized);
};

const isPointing = (direction) => {
    const { leftHand, rightHand, poses } = window.humanDetectionData || {};

    if (!leftHand || !rightHand || !poses?.[0]?.keypoints) {
        return false;
    }

    const scoreThreshold = 0.2;
    const noseY = poses[0].keypoints[0].position.y;
    const hipY = poses[0].keypoints[11].position.y;
    const leftShoulderX = poses[0].keypoints[5].position.x;
    const rightShoulderX = poses[0].keypoints[6].position.x;

    switch (direction) {
        case 'up':
            return (leftHand.score > scoreThreshold && leftHand.position.y < noseY) ||
                (rightHand.score > scoreThreshold && rightHand.position.y < noseY);
        case 'down':
            return (leftHand.score > scoreThreshold && leftHand.position.y > hipY) ||
                (rightHand.score > scoreThreshold && rightHand.position.y > hipY);
        case 'left':
            return (rightHand.score > scoreThreshold && rightHand.position.x < rightShoulderX);
        case 'right':
            return (leftHand.score > scoreThreshold && leftHand.position.x > leftShoulderX);
        default:
            return false;
    }
};

function getFacePart(partName = 'nose') {
    const parts = window.humanDetectionData?.faceParts || {};
    const normalize = (p) => {
        const map = {
            "left eye": "leftEye",
            "right eye": "rightEye",
            "between eyes": "betweenEyes",
            "left ear": "leftEar",
            "right ear": "rightEar",
            "top of head": "topOfHead",
        };
        if (typeof p !== 'string') return 'nose';
        return map[p] || p;
    };
    const key = normalize(partName);
    return parts[key] || null;
}

function getFingerJointPosition(finger = DEFAULT_FINGER) {
    const humanData = window.humanDetectionData || {};
    const rawLandmarks = humanData.handLandmarks;
    if (!rawLandmarks) {
        return null;
    }

    const videoWidth = humanData.videoWidth || 0;
    const videoHeight = humanData.videoHeight || 0;
    if (!videoWidth || !videoHeight) {
        return null;
    }

    const normalizedFinger = normalizeFinger(finger);
    const landmarks = Array.isArray(rawLandmarks?.[0]) ? rawLandmarks[0] : rawLandmarks;
    const index = getHandLandmarkIndex(normalizedFinger);
    const jointPoint = landmarks?.[index];

    if (!jointPoint || typeof jointPoint.x !== 'number' || typeof jointPoint.y !== 'number') {
        return null;
    }

    return {
        x: jointPoint.x * videoWidth,
        y: jointPoint.y * videoHeight
    };
}

async function playPopSound(sounds) {
    const popSources = [
        sounds?.pop,
        './assets/sounds/pop.mp3',
        'assets/sounds/pop.mp3',
        '/assets/sounds/pop.mp3',
        'sounds/pop.mp3'
    ];

    for (const src of popSources) {
        if (src) {
            try {
                const audio = new Audio(src);
                audio.volume = 0.8;
                connectAudioElementToRecording(audio);
                await audio.play();
                return;
            } catch (err) {
                continue;
            }
        }
    }

    await playFrequencySound(800, 200);
}

// ?? MAIN ENHANCED RUN FUNCTION WITH DYNAMIC PATH FOLLOWING
export async function run(actor, dispatch, sounds, selectedActorId, options = {}) {
    if (!actor) {
        console.warn("?? run() called with no actor!");
        return;
    }

    // Clone actor so local adjustments (x/y/direction) don't mutate the frozen Redux state object
    actor = { ...actor };

    if (!Array.isArray(actor.scripts) || actor.scripts.length === 0) {
        console.log(`?? Actor has no scripts to run`);
        return;
    }

    const runId = `run-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    let cardModeActivatedByRun = false;
    let qrScanActivatedByRun = false;
    let numberDetectActivatedByRun = false;

    let currentSpeedMultiplier = 1;
    let currentSpeechLang = (actor?.speechSettings?.lang || DEFAULT_TTS_LANGUAGE).trim();
    const ttsTranslationCache = new Map();
    let obstacleCollisionDetected = false;

    console.log(`?? Starting script execution with ${actor.scripts.length} blocks`);
    console.log(`?? Actor type: ${actor.type || 'stage-actor'}`);
    console.log(`?? Actor position: x: ${actor.x}, y: ${actor.y}, direction: ${actor.direction}`);

    shouldStop = false;
    const stopPalmControl = startPalmControlSession(actor.scripts);

    const allowedStartIds = Array.isArray(options?.startBlockIds) && options.startBlockIds.length
        ? new Set(options.startBlockIds)
        : null;

    const triggerSource = options?.triggerSource || 'manual';
    const allowMessageBroadcast = triggerSource !== 'message';

    const runStartIds = allowedStartIds ? Array.from(allowedStartIds) : [];
    const releaseStartRun = () => {
        if (runStartIds.length) {
            runStartIds.forEach(id => activeStartRuns.delete(id));
        }
    };
    if (runStartIds.length) {
        runStartIds.forEach(id => activeStartRuns.add(id));
    }

    const stacks = [];
    const hasConnections = actor.scripts.some(block => block?.nextBlockId);
    const orderedScripts = hasConnections
        ? actor.scripts
        : [...actor.scripts].sort((a, b) => {
            const ay = Number.isFinite(a?.y) ? a.y : 0;
            const by = Number.isFinite(b?.y) ? b.y : 0;
            if (ay !== by) return ay - by;
            const ax = Number.isFinite(a?.x) ? a.x : 0;
            const bx = Number.isFinite(b?.x) ? b.x : 0;
            return ax - bx;
        });

    const buildChain = (startId, blockMap) => {
        const chain = [];
        const visited = new Set();
        let current = blockMap.get(startId);
        while (current && !visited.has(current.id)) {
            chain.push(current);
            visited.add(current.id);
            const nextId = current.nextBlockId;
            if (!nextId) break;
            current = blockMap.get(nextId);
        }
        return chain;
    };

    if (hasConnections) {
        const blockMap = new Map(actor.scripts.map(block => [block.id, block]));
        const prevMap = new Map();
        actor.scripts.forEach(block => {
            if (block?.nextBlockId) {
                prevMap.set(block.nextBlockId, block.id);
            }
        });

        const startBlocks = actor.scripts.filter(block => isStartTriggerBlock(block));
        const includeStart = (id) => !allowedStartIds || allowedStartIds.has(id);

        startBlocks.forEach(block => {
            if (!includeStart(block.id)) return;
            stacks.push(buildChain(block.id, blockMap));
        });

        if (!startBlocks.length) {
            const heads = actor.scripts.filter(block => !prevMap.has(block.id));
            heads.forEach(block => {
                stacks.push(buildChain(block.id, blockMap));
            });
        }
    } else {
        let includeCurrent = !allowedStartIds;
        let currentStack = [];

        for (const block of orderedScripts) {
            if (isStartTriggerBlock(block)) {
                if (currentStack.length) stacks.push(currentStack);
                currentStack = [];
                includeCurrent = !allowedStartIds || allowedStartIds.has(block.id);

                // ? FIX: Add the start block to its own stack!
                if (includeCurrent) {
                    currentStack.push(block);
                }

                continue;
            }

            if (!includeCurrent) continue;
            currentStack.push(block);
        }

        if (currentStack.length) stacks.push(currentStack);
    }

    if (allowedStartIds && stacks.length === 0) {
        console.log(`?? No matching start blocks for trigger.`);
        return;
    }

    if (!stacks.length) {
        stacks.push(orderedScripts.filter(block => !isStartTriggerBlock(block)));
    }

    const listenForVoiceCommand = (timeoutMs = 8000) => {
        if (!voiceManager?.startListening) {
            console.error('Voice recognition is not available in this browser.');
            return Promise.resolve(null);
        }

        return new Promise((resolve) => {
            let finished = false;
            let timeoutId = null;
            let stopCheckId = null;
            let startedListening = false;

            const finish = (result) => {
                if (finished) return;
                finished = true;
                if (timeoutId) clearTimeout(timeoutId);
                if (stopCheckId) clearInterval(stopCheckId);
                if (startedListening) {
                    voiceManager.stopListening?.();
                }
                resolve(result);
            };

            startedListening = voiceManager.startListening((transcript) => {
                const parsed = voiceManager.parseCommand ? voiceManager.parseCommand(transcript) : null;
                finish(parsed);
            });

            if (!startedListening) {
                finish(null);
                return;
            }

            timeoutId = setTimeout(() => finish(null), timeoutMs);
            stopCheckId = setInterval(() => {
                if (shouldStop) {
                    finish(null);
                }
            }, 200);
        });
    };

    // ??? NEW RECURSIVE FUNCTION FOR EXECUTING SCRIPTS ???
    // ??? NEW RECURSIVE FUNCTION FOR EXECUTING SCRIPTS ???
    const reportScriptTextError = (message) => {
        console.warn(message);
        if (typeof alert === "function") {
            alert(message);
        }
    };

    async function runScriptTextCommands(commands, actor) {
        if (!Array.isArray(commands) || !commands.length) {
            return null;
        }

        for (const command of commands) {
            if (shouldStop || obstacleCollisionDetected) {
                return null;
            }

            switch (command.type) {
                case "move": {
                    const nameMap = {
                        right: "Move Right",
                        left: "Move Left",
                        up: "Move Up",
                        down: "Move Down"
                    };
                    const blockName = nameMap[command.direction];
                    if (!blockName) {
                        return `Line ${command.line}: expected direction (right/left/up/down)`;
                    }
                    await executeScript([{ name: blockName, count: command.value }]);
                    break;
                }
                case "turn": {
                    const degrees = command.direction === "left" ? -command.value : command.value;
                    if (actor.type === "simulatorRobot") {
                        const baseDirection = Number.isFinite(actor.direction) ? actor.direction : 0;
                        const newDirection = (baseDirection + degrees + 360) % 360;
                        moveRobotUniversal(actor, dispatch, actor.x, actor.y, newDirection);
                        actor.direction = newDirection;
                    } else {
                        dispatch(rotateActor({ actorId: actor.id, degrees, fromScript: true }));
                    }
                    await delay(120, currentSpeedMultiplier);
                    break;
                }
                case "wait": {
                    await delay(command.value * 1000, currentSpeedMultiplier);
                    break;
                }
                case "say": {
                    await executeScript([{ name: "Say", type: "say", message: command.value }]);
                    break;
                }
                case "go": {
                    const bounds = getActorGridBounds(actor);
                    if (command.x < bounds.minX || command.x > bounds.maxX) {
                        return `Line ${command.line}: number out of range (${bounds.minX}..${bounds.maxX})`;
                    }
                    if (command.y < bounds.minY || command.y > bounds.maxY) {
                        return `Line ${command.line}: number out of range (${bounds.minY}..${bounds.maxY})`;
                    }

                    if (actor.type === "simulatorRobot") {
                        if (checkSimulatorObstacle(actor, command.x, command.y)) {
                            try {
                                await playFrequencySound(800, 600);
                            } catch (e) {
                                console.warn("Sound play error:", e);
                            }
                            obstacleCollisionDetected = true;
                            return null;
                        }
                        moveRobotUniversal(actor, dispatch, command.x, command.y, actor.direction);
                        actor.x = command.x;
                        actor.y = command.y;
                    } else {
                        dispatch(setActorPosition({
                            actorId: actor.id,
                            x: command.x,
                            y: command.y,
                            fromScript: true
                        }));
                        actor.x = command.x;
                        actor.y = command.y;
                    }
                    await delay(100, currentSpeedMultiplier);
                    break;
                }
                default:
                    return `Line ${command.line}: unknown command "${command.type}"`;
            }
        }

        return null;
    }

    async function executeScript(blocks) {
        for (let i = 0; i < blocks.length; i++) {
            const b = blocks[i];
            await yieldIfRecording();
            // ? Hat blocks are only triggers; skip them during execution
            if (isStartTriggerBlock(b)) {
                console.log(`?? Skipping start block: ${b.name}`);
                continue;
            }


            if (shouldStop) {
                console.log(`Script stopped manually by user.`);
                return;
            }

            if (shouldStop) {
                console.log("?? Script stopped manually by user.");
                return;
            }

            if (obstacleCollisionDetected) {
                console.log('?? SCRIPT EXECUTION STOPPED DUE TO OBSTACLE COLLISION');
                break;
            }

            if (isStopBlock(b)) {
                console.log("?? Stop block reached - halting current stack.");
                shouldStop = true;
                return;
            }

            const c = Math.max(1, Math.min(99, b?.count || 1));

            // ? ADD THIS DEBUG HERE
            console.log(`?? Checking Send Message: type="${b.type}" vs "${MESSAGESENDTYPE}"`);
            console.log(`?? Name check: name="${b.name}"`);

            // ====== ? HANDLE REPEAT BLOCK (NEW!) ======
            if (b.type === "repeat" && b.children && b.children.length > 0) {
                const repeatCount = Math.max(1, Math.min(99, b.count || 4));
                console.log(`?? REPEAT BLOCK: Executing ${b.children.length} blocks ${repeatCount} times`);

                for (let r = 0; r < repeatCount; r++) {
                    console.log(`  ?? Iteration ${r + 1}/${repeatCount}`);

                    // Recursively execute children
                    await executeScript(b.children);

                    if (shouldStop || obstacleCollisionDetected) {
                        return;
                    }
                }

                await delay(80, currentSpeedMultiplier);
                continue;
            }
            // ====== END REPEAT BLOCK ======

            // <-- INSERTION: handle Set Video Transparency early in the main loop
            if (b?.name === 'Set Video Transparency' || b?.type === 'video_transparency') {
                const opacity = Math.max(0, Math.min(100, b?.opacity ?? 100));
                console.log(`?? SETTING VIDEO TRANSPARENCY to ${opacity}%`);
                dispatch(setVideoOpacity(opacity));
                if (window.humanDetectionData) {
                    window.humanDetectionData.videoOpacity = opacity;
                }
                await delay(100, currentSpeedMultiplier);
                continue;
            }
            // <-- END INSERTION

            if (isSpeedBlock(b)) {
                currentSpeedMultiplier = b?.speedMultiplier || 1.5;
                console.log(`? SPEED CHANGED to ${currentSpeedMultiplier}x`);
                continue;
            }

            if (isWaitBlock(b)) {
                const waitTime = (b?.count || 3) * 1000;
                console.log(`? WAITING for ${waitTime}ms`);
                await delay(waitTime, currentSpeedMultiplier);
                continue;
            }

            if (b?.type === "scriptText") {
                const rawScript = typeof b.scriptText === "string" ? b.scriptText : "";
                const parsed = parseScriptText(rawScript);
                if (parsed.error) {
                    reportScriptTextError(parsed.error);
                    return;
                }
                const commandError = await runScriptTextCommands(parsed.commands, actor);
                if (commandError) {
                    reportScriptTextError(commandError);
                    return;
                }
                continue;
            }

            if (b?.type === "qrRunCommands") {
                const rawScript = typeof actor?.qrLastScan === "string" ? actor.qrLastScan : "";
                const trimmed = rawScript.trim();
                if (!trimmed) {
                    continue;
                }
                const parsed = parseScriptText(trimmed);
                if (parsed.error) {
                    console.warn(`QR command script error: ${parsed.error}`);
                    continue;
                }
                const commandError = await runScriptTextCommands(parsed.commands, actor);
                if (commandError) {
                    console.warn(`QR command script error: ${commandError}`);
                    continue;
                }
                continue;
            }

            if (b?.type === 'voice_command' || b?.name === 'Voice Command') {
                console.log('?? Listening for voice command...');
                const command = await listenForVoiceCommand();

                if (shouldStop) {
                    return;
                }

                if (command?.action) {
                    console.log(`?? Voice command -> ${command.action} (${command.count || 1})`);
                    await executeScript([{ name: command.action, count: command.count }]);
                } else {
                    console.warn('?? No valid voice command detected.');
                }
                continue;
            }

            if (isObstacleDetectedBlock(b)) {
                const lowFreq = Math.max(1, Math.min(99, b?.lowFrequency || 1));
                const highFreq = Math.max(1, Math.min(99, b?.highFrequency || 99));
                const alertFrequency = Math.max(lowFreq, highFreq);
                console.log(`?? OBSTACLE DETECTED BLOCK - Playing ${alertFrequency}Hz sound`);
                await playFrequencySound(alertFrequency, 500);
                continue;
            }

            // ?? DYNAMIC PATH FOLLOWING BLOCKS
            if (isSetPathColorBlock(b)) {
                const pathColor = b?.pathColor || b?.color || '#000000';
                console.log(`?? Setting dynamic path following color: ${pathColor}`);

                window.targetPathColor = pathColor.toUpperCase();
                console.log("? Path Color set to", window.targetPathColor);

                continue;
            }

            if (b.type === MESSAGESENDTYPE || b.name === 'Send Message') {
                if (!allowMessageBroadcast) {
                    console.log('?? Skipping Send Message because script was triggered by a message');
                    continue;
                }
                const messageColor = normalizeMessageColor(b?.messageColor);
                console.log(`?? SENDING MESSAGE (color=${messageColor})`);
                triggerMessageBroadcast(messageColor, dispatch);
                await delay(60, currentSpeedMultiplier);
                continue;
            }


            if (b.type === 'goToPage' || b.name === 'Go To Page') {
                const targetRaw = Math.floor(b?.count || 1);
                const normalizedPage = Math.max(1, targetRaw);
                const sceneState = getSceneState(dispatch)?.scene;
                const totalScenes = sceneState?.scenes?.length || 1;
                const targetIndex = Math.min(normalizedPage - 1, totalScenes - 1);
                console.log(`?? GO TO PAGE block -> switching to scene index ${targetIndex}`);
                dispatch(switchScene(targetIndex));
                await waitForReduxUpdate();
                await runSceneGreenFlagStacks(targetIndex, dispatch);
                return;
            }

            if (!window.targetPathColor) {
                window.targetPathColor = "#000000";
                console.log("Defaulting target path color to BLACK (#000000)");
            }
            // ======================================================================================
            // ?? OPEN ROBERTA LAB STYLE LINE FOLLOWER
            // ======================================================================================
            if (isPathFollowingBlock(b)) {
                console.log("?? REAL COLOR-BASED LINE FOLLOWING ACTIVE");
                // ?? Wait for canvas to be ready before starting
                let canvasAttempts = 0;
                while (canvasAttempts < 20) { // Max 2 seconds wait
                    const testPixel = sampleRGBAt(10, 7);
                    // Check if we're getting real colors (not all white with alpha 255)
                    if (testPixel && (testPixel.r !== 255 || testPixel.g !== 255 || testPixel.b !== 255)) {
                        console.log('? Canvas ready, starting line follower');
                        break;
                    }
                    console.log('? Waiting for canvas...', canvasAttempts);
                    await delay(100);
                    canvasAttempts++;
                }

                if (canvasAttempts >= 20) {
                    console.error('? Canvas not ready after 2s, line follower may not work correctly');
                }

                const step = 0.5; // smaller step to avoid overshooting tight curves
                const forwardOffset = 0.5;
                const sideOffset = 0.55; // bring sensors closer for tight bends
                const baseTurnAngle = 14;
                const searchNudge = step * 0.4; // tiny forward creep during search

                const target = (window.targetPathColor || "#000000").toUpperCase();

                while (!shouldStop) {
                    await yieldIfRecording();
                    const x = actor.x;
                    const y = actor.y;
                    const rad = (actor.direction * Math.PI) / 180;

                    // CORRECT ORL coordinate system: y grows downward
                    const fx = Math.cos(rad);
                    const fy = Math.sin(rad);    // FIXED

                    // ORL-CORRECT LEFT & RIGHT DIRECTION VECTORS
                    const leftX = Math.cos(rad - Math.PI / 2);
                    const leftY = Math.sin(rad - Math.PI / 2);
                    const rightX = Math.cos(rad + Math.PI / 2);
                    const rightY = Math.sin(rad + Math.PI / 2);

                    const cx = x + fx * forwardOffset;
                    const cy = y + fy * forwardOffset;

                    // Sensors positioned using correct ORL geometry
                    const lx = cx + leftX * sideOffset;
                    const ly = cy + leftY * sideOffset;

                    const rx = cx + rightX * sideOffset;
                    const ry = cy + rightY * sideOffset;

                    const c = sampleRGBAt(cx, cy);
                    const l = sampleRGBAt(lx, ly);
                    const r = sampleRGBAt(rx, ry);
                    console.log("Center:", c, "Left:", l, "Right:", r, "Target:", target);

                    // Replace existing rgbToHex with this (inside runScript.js)
                    function rgbToHex(col) {
                        if (!col) return "#FFFFFF";
                        const rr = Math.round(Math.max(0, Math.min(255, col.r)));
                        const gg = Math.round(Math.max(0, Math.min(255, col.g)));
                        const bb = Math.round(Math.max(0, Math.min(255, col.b)));

                        const rHex = rr.toString(16).padStart(2, "0");
                        const gHex = gg.toString(16).padStart(2, "0");
                        const bHex = bb.toString(16).padStart(2, "0");

                        return `#${rHex}${gHex}${bHex}`.toUpperCase();
                    }

                    const cHex = rgbToHex(c);
                    const lHex = rgbToHex(l);
                    const rHex = rgbToHex(r);

                    let move = "search";

                    // Accepts two {r,g,b} (or hex), returns true if distance <= threshold
                    function hexToRgbObj(hex) {
                        if (!hex) return null;
                        const h = hex.replace("#", "");
                        return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
                    }

                    function colorDistance(a, b) {
                        if (!a || !b) return Infinity;
                        const dr = (a.r - b.r);
                        const dg = (a.g - b.g);
                        const db = (a.b - b.b);
                        return Math.sqrt(dr * dr + dg * dg + db * db);
                    }

                    const targetRgbObj = hexToRgbObj(target); // compute once per loop before checking

                    function isOnPathHex(hexSample) {
                        if (!hexSample) return false;
                        const sampleRgb = hexToRgbObj(hexSample);
                        // threshold: 40..80 works well; start with 60
                        return colorDistance(sampleRgb, targetRgbObj) <= 60;
                    }

                    console.log(`?? Checking: cHex="${cHex}", lHex="${lHex}", rHex="${rHex}", target="${target}"`);
                    console.log(`? Matches: Center=${isOnPathHex(cHex)}, Left=${isOnPathHex(lHex)}, Right=${isOnPathHex(rHex)}`);

                    let turnAngle = baseTurnAngle;
                    if (isOnPathHex(cHex)) {
                        move = "forward";
                        turnAngle = 4; // gentle correction when centered
                    } else if (isOnPathHex(lHex)) {
                        move = "left";
                    } else if (isOnPathHex(rHex)) {
                        move = "right";
                    }

                    if (move === "forward") {
                        const newX = x + fx * step;
                        const newY = y + fy * step;
                        await smoothMove(actor, dispatch, newX, newY, 1, currentSpeedMultiplier);
                        actor.x = newX;
                        actor.y = newY;
                    } else if (move === "left") {
                        const newDir = (actor.direction - turnAngle + 360) % 360;
                        moveRobotUniversal(actor, dispatch, x, y, newDir);
                        actor.direction = newDir;

                        const newRad = newDir * Math.PI / 180;
                        const newFx = Math.cos(newRad);
                        const newFy = Math.sin(newRad);
                        const newX = x + newFx * (step * 0.5);
                        const newY = y + newFy * (step * 0.5);
                        await smoothMove(actor, dispatch, newX, newY, 1, currentSpeedMultiplier);
                        actor.x = newX;
                        actor.y = newY;
                    } else if (move === "right") {
                        const newDir = (actor.direction + turnAngle) % 360;
                        moveRobotUniversal(actor, dispatch, x, y, newDir);
                        actor.direction = newDir;

                        const newRad = newDir * Math.PI / 180;
                        const newFx = Math.cos(newRad);
                        const newFy = Math.sin(newRad);
                        const newX = x + newFx * (step * 0.5);
                        const newY = y + newFy * (step * 0.5);
                        await smoothMove(actor, dispatch, newX, newY, 1, currentSpeedMultiplier);
                        actor.x = newX;
                        actor.y = newY;
                    } else {
                        // Search mode - just rotate
                        const newDir = (actor.direction + 12) % 360;
                        moveRobotUniversal(actor, dispatch, x, y, newDir);
                        actor.direction = newDir;
                    }

                    await delay(100);
                }

                continue;
            }

            // Regular movement blocks with smooth movement
            const blockIdentifier = b?.name || b?.type;
            console.log(blockIdentifier);

            switch (blockIdentifier) {
                case 'Move Right':
                    if (actor.type === 'simulatorRobot') {
                        const { gridWidth } = getSimGridBounds(dispatch);
                        const targetX = Math.min(actor.x + c, gridWidth - 1);

                        if (!checkSimulatorObstacle(actor, targetX, actor.y)) {
                            if (isConnected()) {
                                try { sendCommand('MOVERIGHT'); } catch (e) { console.warn('sendCommand error:', e); }
                            }
                            await smoothMove(actor, dispatch, targetX, actor.y, 5, currentSpeedMultiplier);
                        } else {
                            console.log('?? COLLISION DETECTED! Playing 800Hz warning sound!');
                            try {
                                await playFrequencySound(800, 600);
                            } catch (e) {
                                console.warn('Sound play error:', e);
                            }
                            obstacleCollisionDetected = true;
                        }
                    } else {
                        const currentScene = getCurrentSceneData(dispatch);
                        if (currentScene && checkForObstacle(actor, 'right', dispatch, currentScene, true)) {
                            console.log('?? STAGE OBSTACLE DETECTED - Playing warning sound!');
                            await playFrequencySound(800, 600);
                            obstacleCollisionDetected = true;
                        } else {
                            dispatch(moveActor({ actorId: actor.id, dx: c, dy: 0, fromScript: true }));
                        }
                    }
                    break;

                case 'Move Left':
                    if (actor.type === 'simulatorRobot') {
                        const targetX = Math.max(actor.x - c, 0);

                        if (!checkSimulatorObstacle(actor, targetX, actor.y)) {
                            if (isConnected()) {
                                try { sendCommand('MOVELEFT'); } catch (e) { console.warn('sendCommand error:', e); }
                            }
                            await smoothMove(actor, dispatch, targetX, actor.y, 5, currentSpeedMultiplier);
                        } else {
                            console.log('?? COLLISION DETECTED! Playing 800Hz warning sound!');
                            try {
                                await playFrequencySound(800, 600);
                            } catch (e) {
                                console.warn('Sound play error:', e);
                            }
                            obstacleCollisionDetected = true;
                        }
                    } else {
                        const currentScene = getCurrentSceneData(dispatch);
                        if (currentScene && checkForObstacle(actor, 'left', dispatch, currentScene, true)) {
                            console.log('?? STAGE OBSTACLE DETECTED - Playing warning sound!');
                            await playFrequencySound(800, 600);
                            obstacleCollisionDetected = true;
                        } else {
                            dispatch(moveActor({ actorId: actor.id, dx: -c, dy: 0, fromScript: true }));
                        }
                    }
                    break;

                case 'Go Home':
                    if (actor.type === 'simulatorRobot') {
                        console.log('?? Robot go home not implemented');
                    } else {
                        console.log("?? Moving actor to home position");
                        dispatch(moveActor({ actorId: actor.id, resetPosition: true, fromScript: true }));
                    }
                    await delay(200, currentSpeedMultiplier);
                    break;

                case 'Hop':
                    if (actor.type === 'simulatorRobot') {
                        console.log('?? Robot hop not implemented');
                    } else {
                        console.log("?? Actor is hopping!");

                        const hopHeight = c || 1;
                        const hopDelay = 150;

                        dispatch(moveActor({ actorId: actor.id, dx: 0, dy: -hopHeight, fromScript: true }));
                        await delay(hopDelay, currentSpeedMultiplier);
                        dispatch(moveActor({ actorId: actor.id, dx: 0, dy: hopHeight, fromScript: true }));
                    }

                    await delay(200, currentSpeedMultiplier);
                    break;

                case 'Go To Random Position':
                    if (actor.type === 'simulatorRobot') {
                        const { gridWidth, gridHeight } = getSimGridBounds(dispatch);
                        const randomX = Math.floor(Math.random() * gridWidth);
                        const randomY = Math.floor(Math.random() * gridHeight);
                        await smoothMove(actor, dispatch, randomX, randomY, 6, currentSpeedMultiplier);
                    } else {
                        const { gridWidth, gridHeight } = getStageGridBounds(dispatch);
                        const randomX = Math.floor(Math.random() * gridWidth);
                        const randomY = Math.floor(Math.random() * gridHeight);
                        dispatch(setActorPosition({
                            actorId: actor.id,
                            x: randomX,
                            y: randomY,
                            fromScript: true
                        }));
                    }
                    await delay(200, currentSpeedMultiplier);
                    break;

                case 'Move Up':
                    if (actor.type === 'simulatorRobot') {
                        const targetY = Math.max(actor.y - c, 0);

                        if (!checkSimulatorObstacle(actor, actor.x, targetY)) {
                            if (isConnected()) {
                                try { sendCommand('MOVEUP'); } catch (e) { console.warn('sendCommand error:', e); }
                            }
                            await smoothMove(actor, dispatch, actor.x, targetY, 5, currentSpeedMultiplier);
                        } else {
                            console.log('?? COLLISION DETECTED! Playing 800Hz warning sound!');
                            try {
                                await playFrequencySound(800, 600);
                            } catch (e) {
                                console.warn('Sound play error:', e);
                            }
                            obstacleCollisionDetected = true;
                        }
                    } else {
                        const currentScene = getCurrentSceneData(dispatch);
                        if (currentScene && checkForObstacle(actor, 'up', dispatch, currentScene, true)) {
                            console.log('?? STAGE OBSTACLE DETECTED - Playing warning sound!');
                            await playFrequencySound(800, 600);
                            obstacleCollisionDetected = true;
                        } else {
                            dispatch(moveActor({ actorId: actor.id, dx: 0, dy: -c, fromScript: true }));
                        }
                    }
                    break;

                case 'Move Down':
                    if (actor.type === 'simulatorRobot') {
                        const { gridHeight } = getSimGridBounds(dispatch);
                        const targetY = Math.min(actor.y + c, gridHeight - 1);

                        if (!checkSimulatorObstacle(actor, actor.x, targetY)) {
                            if (isConnected()) {
                                try { sendCommand('MOVEDOWN'); } catch (e) { console.warn('sendCommand error:', e); }
                            }
                            await smoothMove(actor, dispatch, actor.x, targetY, 5, currentSpeedMultiplier);
                        } else {
                            console.log('?? COLLISION DETECTED! Playing 800Hz warning sound!');
                            try {
                                await playFrequencySound(800, 600);
                            } catch (e) {
                                console.warn('Sound play error:', e);
                            }
                            obstacleCollisionDetected = true;
                        }
                    } else {
                        const currentScene = getCurrentSceneData(dispatch);
                        if (currentScene && checkForObstacle(actor, 'down', dispatch, currentScene, true)) {
                            console.log('?? STAGE OBSTACLE DETECTED - Playing warning sound!');
                            await playFrequencySound(800, 600);
                            obstacleCollisionDetected = true;
                        } else {
                            dispatch(moveActor({ actorId: actor.id, dx: 0, dy: c, fromScript: true }));
                        }
                    }
                    break;

                case 'Rotate Left':
                    for (let k = 0; k < c; k++) {
                        if (actor.type === 'simulatorRobot') {
                            if (isConnected()) {
                                try { sendCommand("ROTATE_LEFT"); } catch (e) { console.warn('sendCommand error', e); }
                            }
                            const newDirection = (actor.direction - 90 + 360) % 360;
                            moveRobotUniversal(actor, dispatch, actor.x, actor.y, newDirection);
                        } else {
                            dispatch(rotateActor({ actorId: actor.id, degrees: -90, fromScript: true }));
                        }
                        await delay(140, currentSpeedMultiplier);
                    }
                    break;

                case 'Rotate Right':
                    for (let k = 0; k < c; k++) {
                        if (actor.type === 'simulatorRobot') {
                            if (isConnected()) {
                                try { sendCommand("ROTATE_RIGHT"); } catch (e) { console.warn('sendCommand error', e); }
                            }
                            const newDirection = (actor.direction + 90) % 360;
                            moveRobotUniversal(actor, dispatch, actor.x, actor.y, newDirection);
                        } else {
                            dispatch(rotateActor({ actorId: actor.id, degrees: 90, fromScript: true }));
                        }
                        await delay(140, currentSpeedMultiplier);
                    }
                    break;

                // ?? ======== ADD ROBOT BLOCKS HERE ======== ??

                case "Move Forward":
                case "robot_move_forward": {
                    if (b?.type === "sim_move_forward") {
                        if (actor.type !== "simulatorRobot") break;
                        const steps = Math.max(1, c || 1);
                        for (let step = 0; step < steps; step++) {
                            if (shouldStop || obstacleCollisionDetected) break;
                            const freshState = getSceneState(dispatch);
                            const freshRobot = freshState?.scene?.simulatorRobots?.find(r => r.id === actor.id);
                            if (!freshRobot) break;

                            const { dx, dy, angle } = normalizeSimDirection(freshRobot.direction);
                            const targetX = freshRobot.x + dx;
                            const targetY = freshRobot.y + dy;

                            const { gridWidth, gridHeight } = getSimGridBounds(dispatch);
                            if (targetX < 0 || targetX >= gridWidth || targetY < 0 || targetY >= gridHeight) {
                                break;
                            }

                            if (!checkSimulatorObstacle(freshRobot, targetX, targetY)) {
                                actor.direction = angle;
                                await smoothMove(actor, dispatch, targetX, targetY, 5, currentSpeedMultiplier);
                                actor.x = targetX;
                                actor.y = targetY;
                            } else {
                                console.log('?? COLLISION DETECTED! Playing 800Hz warning sound!');
                                try {
                                    await playFrequencySound(800, 600);
                                } catch (e) {
                                    console.warn('Sound play error:', e);
                                }
                                obstacleCollisionDetected = true;
                                break;
                            }
                        }
                        break;
                    }

                    if (actor.type === "simulatorRobot") {
                        const steps = b.count || 20;
                        const freshState = getSceneState(dispatch);
                        const freshRobot = freshState?.scene?.simulatorRobots?.find(r => r.id === actor.id);

                        if (freshRobot) {
                            const direction = freshRobot.direction || 0;
                            const radians = (direction * Math.PI) / 180;
                            const { gridWidth, gridHeight } = getSimGridBounds(dispatch);

                            for (let i = 0; i < steps; i++) {
                                if (shouldStop) break;

                                const newX = freshRobot.x + Math.cos(radians) * 0.5;
                                const newY = freshRobot.y + Math.sin(radians) * 0.5;

                                if (newX < 0 || newX > gridWidth - 1 || newY < 0 || newY > gridHeight - 1) break;

                                if (checkSimulatorObstacle(freshRobot, Math.round(newX), Math.round(newY))) {
                                    obstacleCollisionDetected = true;
                                    break;
                                }

                                moveRobotUniversal(actor, dispatch, newX, newY, direction);
                                await delay(50, currentSpeedMultiplier);
                            }
                        }
                    }
                    break;
                }

                case "Move Backward":
                case "robot_move_backward": {
                    if (b?.type === "sim_move_backward") {
                        if (actor.type !== "simulatorRobot") break;
                        const steps = Math.max(1, c || 1);
                        for (let step = 0; step < steps; step++) {
                            if (shouldStop || obstacleCollisionDetected) break;
                            const freshState = getSceneState(dispatch);
                            const freshRobot = freshState?.scene?.simulatorRobots?.find(r => r.id === actor.id);
                            if (!freshRobot) break;

                            const { dx, dy, angle } = normalizeSimDirection(freshRobot.direction);
                            const targetX = freshRobot.x - dx;
                            const targetY = freshRobot.y - dy;

                            const { gridWidth, gridHeight } = getSimGridBounds(dispatch);
                            if (targetX < 0 || targetX >= gridWidth || targetY < 0 || targetY >= gridHeight) {
                                break;
                            }

                            if (!checkSimulatorObstacle(freshRobot, targetX, targetY)) {
                                actor.direction = angle;
                                await smoothMove(actor, dispatch, targetX, targetY, 5, currentSpeedMultiplier);
                                actor.x = targetX;
                                actor.y = targetY;
                            } else {
                                console.log('?? COLLISION DETECTED! Playing 800Hz warning sound!');
                                try {
                                    await playFrequencySound(800, 600);
                                } catch (e) {
                                    console.warn('Sound play error:', e);
                                }
                                obstacleCollisionDetected = true;
                                break;
                            }
                        }
                        break;
                    }

                    if (actor.type === "simulatorRobot") {
                        const steps = b.count || 20;
                        const freshState = getSceneState(dispatch);
                        const freshRobot = freshState?.scene?.simulatorRobots?.find(r => r.id === actor.id);

                        if (freshRobot) {
                            const direction = freshRobot.direction || 0;
                            const radians = (direction * Math.PI) / 180;
                            const { gridWidth, gridHeight } = getSimGridBounds(dispatch);

                            for (let i = 0; i < steps; i++) {
                                if (shouldStop) break;

                                const newX = freshRobot.x - Math.cos(radians) * 0.5;
                                const newY = freshRobot.y - Math.sin(radians) * 0.5;

                                if (newX < 0 || newX > gridWidth - 1 || newY < 0 || newY > gridHeight - 1) break;
                                if (checkSimulatorObstacle(freshRobot, Math.round(newX), Math.round(newY))) {
                                    obstacleCollisionDetected = true;
                                    break;
                                }

                                moveRobotUniversal(actor, dispatch, newX, newY, direction);
                                await delay(50, currentSpeedMultiplier);
                            }
                        }
                    }
                    break;
                }

                case "Turn Left":
                case "robot_turn_left": {
                    if (b?.type === "sim_turn_left") {
                        if (actor.type !== "simulatorRobot") break;
                        const freshState = getSceneState(dispatch);
                        const freshRobot = freshState?.scene?.simulatorRobots?.find(r => r.id === actor.id);
                        if (!freshRobot) break;

                        const { angle } = normalizeSimDirection(freshRobot.direction);
                        const newDirection = (angle - 90 + 360) % 360;
                        moveRobotUniversal(actor, dispatch, freshRobot.x, freshRobot.y, newDirection);
                        actor.direction = newDirection;
                        await delay(140, currentSpeedMultiplier);
                        break;
                    }

                    if (actor.type === "simulatorRobot") {
                        const degrees = b.count || 90;
                        const freshState = getSceneState(dispatch);
                        const freshRobot = freshState?.scene?.simulatorRobots?.find(r => r.id === actor.id);

                        if (freshRobot) {
                            const currentDirection = freshRobot.direction || 0;
                            const newDirection = (currentDirection - degrees + 360) % 360;
                            moveRobotUniversal(actor, dispatch, freshRobot.x, freshRobot.y, newDirection);
                            actor.direction = newDirection;
                        }
                    }
                    await delay(300, currentSpeedMultiplier);
                    break;
                }

                case "Turn Right":
                case "robot_turn_right": {
                    if (b?.type === "sim_turn_right") {
                        if (actor.type !== "simulatorRobot") break;
                        const freshState = getSceneState(dispatch);
                        const freshRobot = freshState?.scene?.simulatorRobots?.find(r => r.id === actor.id);
                        if (!freshRobot) break;

                        const { angle } = normalizeSimDirection(freshRobot.direction);
                        const newDirection = (angle + 90) % 360;
                        moveRobotUniversal(actor, dispatch, freshRobot.x, freshRobot.y, newDirection);
                        actor.direction = newDirection;
                        await delay(140, currentSpeedMultiplier);
                        break;
                    }

                    if (actor.type === "simulatorRobot") {
                        const degrees = b.count || 90;
                        const freshState = getSceneState(dispatch);
                        const freshRobot = freshState?.scene?.simulatorRobots?.find(r => r.id === actor.id);

                        if (freshRobot) {
                            const currentDirection = freshRobot.direction || 0;
                            const newDirection = (currentDirection + degrees) % 360;
                            moveRobotUniversal(actor, dispatch, freshRobot.x, freshRobot.y, newDirection);
                            actor.direction = newDirection;
                        }
                    }
                    await delay(300, currentSpeedMultiplier);
                    break;
                }

                case "Stop":
                case "robot_stop": {
                    shouldStop = true;
                    resetRobotServos();
                    break;
                }

                // ?? SERVO CONTROL BLOCKS
                case "Set Left Leg Angle":
                case "robot_servo_left": {
                    const state = ensureRobotServoState();
                    const current = state?.left?.current ?? SERVO_DEFAULT_ANGLE;
                    freezeOtherServo('left');
                    const target = setServoTarget('left', b.count ?? b.angle ?? SERVO_DEFAULT_ANGLE);
                    const waitMs = estimateServoMoveMs(state, current, target);
                    await delay(waitMs, currentSpeedMultiplier);
                    await returnServoToNeutral('left', currentSpeedMultiplier);
                    break;
                }

                case "Set Right Leg Angle":
                case "robot_servo_right": {
                    const state = ensureRobotServoState();
                    const current = state?.right?.current ?? SERVO_DEFAULT_ANGLE;
                    freezeOtherServo('right');
                    const target = setServoTarget('right', b.count ?? b.angle ?? SERVO_DEFAULT_ANGLE);
                    const waitMs = estimateServoMoveMs(state, current, target);
                    await delay(waitMs, currentSpeedMultiplier);
                    await returnServoToNeutral('right', currentSpeedMultiplier);
                    break;
                }

                case "Set Servo Speed":
                case "robot_servo_speed": {
                    setServoSpeed(b.count ?? b.speed ?? SERVO_DEFAULT_SPEED);
                    break;
                }

                case "Walk":
                case "robot_walk": {
                    const state = ensureRobotServoState();
                    if (!state) break;

                    const walkToken = (state.walkToken || 0) + 1;
                    state.walkToken = walkToken;
                    state.walking = true;

                    const steps = Math.max(1, Number(b.count) || 1);
                    const moveForwardTick = () => {
                        if (actor.type !== "simulatorRobot") return;
                        const freshState = getSceneState(dispatch);
                        const freshRobot = freshState?.scene?.simulatorRobots?.find(r => r.id === actor.id);
                        if (!freshRobot) return;
                        const direction = freshRobot.direction || 0;
                        const radians = (direction * Math.PI) / 180;
                        const { gridWidth, gridHeight } = getSimGridBounds(dispatch);
                        const distance = 0.6;
                        const newX = freshRobot.x + Math.cos(radians) * distance;
                        const newY = freshRobot.y + Math.sin(radians) * distance;

                        if (newX < 0 || newX > gridWidth - 1 || newY < 0 || newY > gridHeight - 1) return;
                        if (checkSimulatorObstacle(freshRobot, Math.round(newX), Math.round(newY))) {
                            obstacleCollisionDetected = true;
                            return;
                        }
                        moveRobotUniversal(actor, dispatch, newX, newY, direction);
                    };

                    for (let step = 0; step < steps; step++) {
                        if (shouldStop || obstacleCollisionDetected || state.walkToken !== walkToken) break;

                        const leftFrom = state.left?.current ?? SERVO_DEFAULT_ANGLE;
                        const rightFrom = state.right?.current ?? SERVO_DEFAULT_ANGLE;
                        const leftTarget = setServoTarget('left', SERVO_WALK_FORWARD_ANGLE);
                        const rightTarget = setServoTarget('right', SERVO_WALK_BACK_ANGLE);
                        const waitMsA = Math.max(
                            estimateServoMoveMs(state, leftFrom, leftTarget),
                            estimateServoMoveMs(state, rightFrom, rightTarget)
                        );
                        await delay(waitMsA, currentSpeedMultiplier);
                        moveForwardTick();

                        if (shouldStop || obstacleCollisionDetected || state.walkToken !== walkToken) break;

                        const leftFromB = state.left?.current ?? SERVO_DEFAULT_ANGLE;
                        const rightFromB = state.right?.current ?? SERVO_DEFAULT_ANGLE;
                        const leftTargetB = setServoTarget('left', SERVO_WALK_BACK_ANGLE);
                        const rightTargetB = setServoTarget('right', SERVO_WALK_FORWARD_ANGLE);
                        const waitMsB = Math.max(
                            estimateServoMoveMs(state, leftFromB, leftTargetB),
                            estimateServoMoveMs(state, rightFromB, rightTargetB)
                        );
                        await delay(waitMsB, currentSpeedMultiplier);
                        moveForwardTick();
                    }

                    if (state.walkToken === walkToken) {
                        state.walking = false;
                    }
                    if (!shouldStop && state.walkToken === walkToken) {
                        await delay(SERVO_RETURN_DELAY_MS, currentSpeedMultiplier);
                        const leftFrom = state.left?.current ?? SERVO_DEFAULT_ANGLE;
                        const rightFrom = state.right?.current ?? SERVO_DEFAULT_ANGLE;
                        setServoTarget('left', SERVO_DEFAULT_ANGLE);
                        setServoTarget('right', SERVO_DEFAULT_ANGLE);
                        const waitMs = Math.max(
                            estimateServoMoveMs(state, leftFrom, SERVO_DEFAULT_ANGLE),
                            estimateServoMoveMs(state, rightFrom, SERVO_DEFAULT_ANGLE)
                        );
                        await delay(waitMs, currentSpeedMultiplier);
                    }
                    break;
                }

                // ?? LED CONTROL BLOCKS
                case "LED ON":
                case "robot_led_on": {
                    if (!window.robotLEDState) {
                        window.robotLEDState = { on: false, color: "#FF0000", brightness: 100 };
                    }
                    window.robotLEDState.on = true;
                    window.robotLEDState.color = window.robotLEDState.color || "#FF0000";
                    console.log("?? LED ON:", window.robotLEDState);
                    break;
                }

                case "LED OFF":
                case "robot_led_off": {
                    if (!window.robotLEDState) {
                        window.robotLEDState = { on: false, color: "#FF0000", brightness: 100 };
                    }
                    window.robotLEDState.on = false;
                    console.log("? LED OFF");
                    break;
                }

                // ?? LED RING ON
                case "LED Ring ON":
                case "robot_led_ring_on":
                    console.log("?? LED Ring ON");
                    ensureRobotRingState();
                    window.robotLEDState.ring.mode = "solid";
                    break;

                // ?? LED RING OFF
                case "LED Ring OFF":
                case "robot_led_ring_off":
                    console.log("? LED Ring OFF");
                    ensureRobotRingState();
                    window.robotLEDState.ring.mode = "off";
                    break;

                // ?? SET LED RING COLOR
                case "Set LED Ring Color":
                case "robot_led_ring_color":
                    const ringColor = b.ledRingColor || b.color || "#00FF00";
                    console.log("?? LED Ring Color set to:", ringColor);
                    setRingAllColor(ringColor);
                    break;

                // ?? LED RING RAINBOW
                case "LED Ring Rainbow":
                case "robot_led_ring_rainbow":
                    console.log("?? LED Ring Rainbow Mode!");
                    ensureRobotRingState();
                    window.robotLEDState.ring.mode = "rainbow";
                    break;

                // ?? LED RING SET INDEX COLOR
                case "Set Ring LED Color":
                case "robot_led_ring_index": {
                    const ringIndex = Math.max(0, Math.min(11, Number(b.count) || 0));
                    const ringIndexColor = b.ledRingColor || b.color || "#00FF00";
                    console.log(`?? LED Ring index ${ringIndex} color set to:`, ringIndexColor);
                    setRingLedIndexColor(ringIndex, ringIndexColor);
                    break;
                }

                // ?? LED RING SET ALL
                case "Set All Ring LEDs":
                case "robot_led_ring_all": {
                    const ringAllColor = b.ledRingColor || b.color || "#00FF00";
                    console.log("?? LED Ring set all to:", ringAllColor);
                    setRingAllColor(ringAllColor);
                    break;
                }


                case "robot_led_color":
                case "robot_led":
                case "Set LED Color": {
                    console.log("?? FULL BLOCK DATA:", JSON.stringify(b, null, 2)); // ? USE 'block'
                    const selectedColor = b.ledColor || b.color || "#FF0000";
                    console.log("?? LED Color set to:", selectedColor);

                    if (!window.robotLEDState) {
                        window.robotLEDState = {
                            on: true,
                            color: selectedColor,
                            brightness: 100
                        };
                    } else {
                        window.robotLEDState.color = selectedColor;
                        window.robotLEDState.on = true; // Turn ON when setting color
                    }

                    console.log("?? Updated window.robotLEDState:", window.robotLEDState);
                    break;
                }


                case "Set LED Brightness":
                case "robot_led_brightness": {
                    const brightness = Math.max(0, Math.min(100, b.count || 100));
                    if (!window.robotLEDState) {
                        window.robotLEDState = { on: false, color: "#FF0000", brightness: 100 };
                    }
                    window.robotLEDState.brightness = brightness;
                    console.log("?? LED Brightness set to:", brightness + "%");
                    break;
                }

                // ?? SOUND BLOCKS
                case "Play Tone":
                case "robot_play_tone": {
                    const frequency = b.frequency || 440;
                    const duration = b.duration || 500;
                    await playFrequencySound(frequency, duration);
                    break;
                }

                case "Play Buzzer":
                case "robot_buzzer": {
                    await playFrequencySound(800, 200);
                    break;
                }

                // ?? SENSOR BLOCKS
                case "Distance Sensor":
                case "robot_distance": {
                    const distance = Math.floor(Math.random() * 100);
                    console.log("?? Distance:", distance, "cm");
                    break;
                }

                // ?? ======== END ROBOT BLOCKS ======== ??

                // 🤖 ======== STEMBOT (AI) BLOCKS ======== 🤖
                case "Set AI Persona":
                case "aiSetPersona": {
                    const persona = typeof b?.persona === "string" ? b.persona : "ai";
                    if (actor) {
                        actor.aiPersona = persona;
                    }
                    const payload = { data: { aiPersona: persona } };
                    if (actor?.type === "simulatorRobot") {
                        payload.robotId = actor.id;
                    } else {
                        payload.actorId = actor.id;
                    }
                    dispatch(updateActorCustomData(payload));
                    break;
                }

                case "Set Word Limit":
                case "aiWordLimit": {
                    const rawLimit = Number(b?.count ?? 40);
                    const wordLimit = Math.max(1, Math.min(200, Number.isFinite(rawLimit) ? rawLimit : 40));
                    if (actor) {
                        actor.aiWordLimit = wordLimit;
                    }
                    const payload = { data: { aiWordLimit: wordLimit } };
                    if (actor?.type === "simulatorRobot") {
                        payload.robotId = actor.id;
                    } else {
                        payload.actorId = actor.id;
                    }
                    dispatch(updateActorCustomData(payload));
                    break;
                }

                case "Set Creativity":
                case "aiCreativity": {
                    const rawValue = Number(b?.count ?? 0.7);
                    const baseValue = Number.isFinite(rawValue) ? rawValue : 0.7;
                    const normalized = baseValue > 1 ? baseValue / 100 : baseValue;
                    const temperature = Math.max(0, Math.min(1, normalized));
                    if (actor) {
                        actor.aiTemperature = temperature;
                    }
                    const payload = { data: { aiTemperature: temperature } };
                    if (actor?.type === "simulatorRobot") {
                        payload.robotId = actor.id;
                    } else {
                        payload.actorId = actor.id;
                    }
                    dispatch(updateActorCustomData(payload));
                    break;
                }

                case "Set Prompt":
                case "aiPrompt": {
                    const prompt = String(b?.message || "").trim();
                    if (actor) {
                        actor.aiLastPrompt = prompt;
                        actor.aiPromptSource = "text";
                    }
                    const payload = { data: { aiLastPrompt: prompt, aiPromptSource: "text" } };
                    if (actor?.type === "simulatorRobot") {
                        payload.robotId = actor.id;
                    } else {
                        payload.actorId = actor.id;
                    }
                    dispatch(updateActorCustomData(payload));
                    break;
                }

                case "Speech To Text":
                case "speechToText": {
                    const transcript = await listenForSpeech();
                    const safeTranscript = String(transcript || "").trim();
                    if (actor) {
                        actor.aiLastPrompt = safeTranscript;
                        actor.aiPromptSource = "speech";
                    }
                    const payload = { data: { aiLastPrompt: safeTranscript, aiPromptSource: "speech" } };
                    if (actor?.type === "simulatorRobot") {
                        payload.robotId = actor.id;
                    } else {
                        payload.actorId = actor.id;
                    }
                    dispatch(updateActorCustomData(payload));

                    if (safeTranscript && actor?.type !== "simulatorRobot") {
                        dispatch(showSpriteMessage({ actorId: actor.id, message: safeTranscript }));
                        await delay(3000, currentSpeedMultiplier);
                        dispatch(hideSpriteMessage({ actorId: actor.id }));
                    }
                    break;
                }

                case "Get Response":
                case "aiGetResponse": {
                    const prompt = String(actor?.aiLastPrompt || b?.message || "").trim();
                    const persona = String(actor?.aiPersona || b?.persona || "ai");
                    const wordLimit = Number.isFinite(actor?.aiWordLimit) ? actor.aiWordLimit : 0;
                    const temperature = Number.isFinite(actor?.aiTemperature) ? actor.aiTemperature : DEFAULT_AI_TEMPERATURE;
                    const requestedCount = inferRequestedCount(prompt);
                    const promptSource = actor?.aiPromptSource || "text";

                    const clearPayload = { data: { aiHighlightWords: [], aiLastResponse: "", aiLastPrompt: prompt } };
                    if (actor?.type === "simulatorRobot") {
                        clearPayload.robotId = actor.id;
                    } else {
                        clearPayload.actorId = actor.id;
                    }
                    dispatch(updateActorCustomData(clearPayload));

                    if (!prompt) {
                        if (actor?.type !== "simulatorRobot") {
                            dispatch(showSpriteMessage({ actorId: actor.id, message: "Add a prompt first." }));
                            await delay(2500, currentSpeedMultiplier);
                            dispatch(hideSpriteMessage({ actorId: actor.id }));
                        }
                        break;
                    }

                    if (actor?.type !== "simulatorRobot") {
                        dispatch(showSpriteMessage({ actorId: actor.id, message: "Thinking..." }));
                    }

                    try {
                        const response = await fetchAiResponse({ prompt, persona, wordLimit, requestedCount, temperature });
                        const trimmedResponse = response || "No response.";
                        const finalResponse = limitWords(trimmedResponse, wordLimit);
                        let displayResponse = finalResponse;
                        let translatedMessage = null;
                        const actorLanguage = (currentSpeechLang || DEFAULT_TTS_LANGUAGE).trim();

                        if (promptSource === "speech" && actorLanguage) {
                            const cacheKey = `${actorLanguage.toLowerCase()}|${finalResponse}`;
                            translatedMessage = ttsTranslationCache.get(cacheKey);
                            if (!translatedMessage && finalResponse.trim()) {
                                try {
                                    translatedMessage = await textTranslator.translate(finalResponse, actorLanguage);
                                    if (translatedMessage) {
                                        ttsTranslationCache.set(cacheKey, translatedMessage);
                                    }
                                } catch (err) {
                                    console.warn('Translation failed, using fallback text:', err);
                                }
                            }
                            displayResponse = translatedMessage || finalResponse;
                        }

                        if (actor) {
                            actor.aiLastResponse = displayResponse;
                            actor.aiHighlightWords = [];
                        }
                        const payload = { data: { aiLastResponse: displayResponse, aiHighlightWords: [] } };
                        if (actor?.type === "simulatorRobot") {
                            payload.robotId = actor.id;
                        } else {
                            payload.actorId = actor.id;
                        }
                        dispatch(updateActorCustomData(payload));

                        if (actor?.type !== "simulatorRobot") {
                            dispatch(showSpriteMessage({ actorId: actor.id, message: displayResponse }));
                        }

                        if (promptSource === "speech") {
                            const speakText = displayResponse;
                            try {
                                await textToSpeechManager.speak(speakText, actorLanguage);
                            } catch (err) {
                                console.warn("Speech playback error:", err);
                            }
                            if (actor) {
                                actor.aiPromptSource = "text";
                            }
                            const resetPayload = { data: { aiPromptSource: "text" } };
                            if (actor?.type === "simulatorRobot") {
                                resetPayload.robotId = actor.id;
                            } else {
                                resetPayload.actorId = actor.id;
                            }
                            dispatch(updateActorCustomData(resetPayload));
                        }
                    } catch (err) {
                        console.warn("AI response failed:", err);
                        if (actor?.type !== "simulatorRobot") {
                            dispatch(showSpriteMessage({ actorId: actor.id, message: "AI request failed." }));
                        }
                    }
                    break;
                }

                case "Highlight POS":
                case "aiHighlightPos": {
                    const responseText = String(actor?.aiLastResponse || "");
                    if (!responseText.trim()) break;
                    const posType = typeof b?.posType === "string" ? b.posType : "noun";
                    const words = extractPosWords(responseText, posType);
                    if (actor) {
                        actor.aiHighlightWords = words;
                        actor.aiHighlightType = posType;
                    }
                    const payload = { data: { aiHighlightWords: words, aiHighlightType: posType } };
                    if (actor?.type === "simulatorRobot") {
                        payload.robotId = actor.id;
                    } else {
                        payload.actorId = actor.id;
                    }
                    dispatch(updateActorCustomData(payload));
                    break;
                }

                case "AI Response":
                case "aiResponse": {
                    const responseText = String(actor?.aiLastResponse || "").trim();
                    if (!responseText) break;
                    if (actor?.type !== "simulatorRobot") {
                        dispatch(showSpriteMessage({ actorId: actor.id, message: responseText }));
                    }
                    break;
                }

                case "Set Language":
                case "ttsSetLanguage": {
                    const desiredLang = (b?.selectedLang || b?.lang || actor?.speechSettings?.lang || DEFAULT_TTS_LANGUAGE).trim();
                    if (actor?.type === 'simulatorRobot') {
                        dispatch(setActorLanguage({ robotId: actor.id, lang: desiredLang }));
                    } else {
                        dispatch(setActorLanguage({ actorId: actor.id, lang: desiredLang }));
                    }
                    currentSpeechLang = desiredLang;
                    break;
                }

                case "Object Detection":
                case "cardMode": {
                    if (!cardModeActivatedByRun) {
                        activeCardModeRuns.add(runId);
                        dispatch(setCardModeEnabled(true));
                        cardModeActivatedByRun = true;
                    }
                    break;
                }

                case "Scan QR/Barcode":
                case "qrBarcodeScan": {
                    if (!qrScanActivatedByRun) {
                        activeQrScanRuns.add(runId);
                        dispatch(setQrScanEnabled(true));
                        qrScanActivatedByRun = true;
                    }
                    break;
                }

                case "Show QR Data":
                case "qrShowData": {
                    const qrText = String(actor?.qrLastScan || "").trim();
                    const message = qrText || "No QR data yet.";
                    const shouldShowBubble = actor?.type !== "simulatorRobot";
                    if (shouldShowBubble && actor?.id) {
                        dispatch(showSpriteMessage({ actorId: actor.id, message }));
                    }
                    const actorLanguage = (currentSpeechLang || DEFAULT_TTS_LANGUAGE).trim();
                    try {
                        await textToSpeechManager.speak(message, actorLanguage);
                    } catch (err) {
                        console.warn("QR speak failed:", err);
                    }
                    break;
                }

                case "Detect Number (Printed)":
                case "numberDetect": {
                    if (!numberDetectActivatedByRun) {
                        activeNumberDetectRuns.add(runId);
                        dispatch(setNumberDetectEnabled(true));
                        numberDetectActivatedByRun = true;
                    }
                    break;
                }

                case "Speak":
                case "ttsSpeak": {
                    const rawText = b?.message ?? b?.parameters?.message ?? b?.value ?? "hi";
                    const fallbackText = typeof rawText === 'string' ? rawText : String(rawText);
                    const actorLanguage = (currentSpeechLang || DEFAULT_TTS_LANGUAGE).trim();

                    const cacheKey = `${actorLanguage.toLowerCase()}|${fallbackText}`;
                    let translatedMessage = ttsTranslationCache.get(cacheKey);

                    if (!translatedMessage && fallbackText.trim()) {
                        try {
                            translatedMessage = await textTranslator.translate(fallbackText, actorLanguage);
                            if (translatedMessage) {
                                ttsTranslationCache.set(cacheKey, translatedMessage);
                            }
                        } catch (err) {
                            console.warn('Translation failed, using fallback text:', err);
                        }
                    }

                    const finalMessage = translatedMessage || fallbackText;
                    const shouldShowBubble = actor?.type !== 'simulatorRobot';
                    if (shouldShowBubble) {
                        dispatch(showSpriteMessage({ actorId: actor.id, message: finalMessage }));
                    }

                    let spokeSuccessfully = false;
                    try {
                        spokeSuccessfully = await textToSpeechManager.speak(finalMessage, actorLanguage);
                    } catch (err) {
                        console.warn('Speech playback error:', err);
                    }

                    if (!spokeSuccessfully) {
                        await delay(5000, currentSpeedMultiplier);
                    }

                    if (shouldShowBubble) {
                        dispatch(hideSpriteMessage({ actorId: actor.id }));
                    }
                    break;
                }

                case "Say":
                case "say": {
                    console.log("Full block object:", JSON.stringify(b, null, 2));
                    console.log("block.message:", b.message);
                    console.log("block.parameters:", b.parameters);
                    console.log("block.value:", b.value);

                    const message = b?.message || b?.parameters?.message || b?.value || "hi";
                    console.log("Final message to display:", message);

                    dispatch(showSpriteMessage({ actorId: actor.id, message }));
                    await delay(5000, currentSpeedMultiplier);
                    dispatch(hideSpriteMessage({ actorId: actor.id }));
                    break;
                }

                case 'Grow Size': {
                    if (actor.type === 'simulatorRobot') {
                        console.log('?? Robot scaling not implemented');
                    } else {
                        const units = Math.max(0, Math.min(50, Number(b?.count) || 2));
                        const delta = units * 0.1;
                        const nextScale = (actor.scale || 1) + delta;
                        console.log(`?? GROW SIZE by ${units} units -> scale ${nextScale}`);
                        dispatch(scaleActor({ actorId: actor.id, scale: nextScale, fromScript: true }));
                        actor.scale = nextScale;
                    }
                    await delay(200, currentSpeedMultiplier);
                    break;
                }

                case 'Shrink Size': {
                    if (actor.type === 'simulatorRobot') {
                        console.log('?? Robot scaling not implemented');
                    } else {
                        const units = Math.max(0, Math.min(50, Number(b?.count) || 2));
                        const delta = units * 0.1;
                        const nextScale = Math.max(0.1, (actor.scale || 1) - delta);
                        console.log(`?? SHRINK SIZE by ${units} units -> scale ${nextScale}`);
                        dispatch(scaleActor({ actorId: actor.id, scale: nextScale, fromScript: true }));
                        actor.scale = nextScale;
                    }
                    await delay(200, currentSpeedMultiplier);
                    break;
                }

                case 'Reset Size':
                    if (actor.type === 'simulatorRobot') {
                        console.log('?? Robot resize not implemented');
                    } else {
                        console.log("resetting to original size");
                        dispatch(scaleActor({ actorId: actor.id, reset: true, fromScript: true }));
                    }
                    await delay(200, currentSpeedMultiplier);
                    break;

                case 'Set Video Transparency':
                    console.log(`?? SETTING VIDEO TRANSPARENCY to ${b?.opacity ?? 100}%`);
                    {
                        const opacity = Math.max(0, Math.min(100, b?.opacity ?? 100));
                        dispatch(setVideoOpacity(opacity));
                        if (window.humanDetectionData) {
                            window.humanDetectionData.videoOpacity = opacity;
                        }
                    }
                    await delay(100, currentSpeedMultiplier);
                    break;

                case 'Camera Control':
                case 'camera':
                case 'camera_control': {
                    // Camera is managed by the UI dropdown + human detection stage.
                    break;
                }

                case 'Disappear':
                    if (actor.type === 'simulatorRobot') {
                        dispatch(disappearSimulatorRobot({ id: actor.id }));
                    } else {
                        dispatch(disappearActor({ actorId: actor.id, fromScript: true }));
                    }
                    await delay(200, currentSpeedMultiplier);
                    break;

                case 'Appear':
                    if (actor.type === 'simulatorRobot') {
                        dispatch(reappearSimulatorRobot({ id: actor.id }));
                    } else {
                        dispatch(reappearActor({ actorId: actor.id, fromScript: true }));
                    }
                    await delay(200, currentSpeedMultiplier);
                    break;

                case "Play Sound":
                case "sound_effect":
                case "Robot Sound":
                case "robot_sound_effect": {
                    const soundType = b?.soundType || "happy";
                    await playSoundEffect(soundType);
                    await delay(120, currentSpeedMultiplier);
                    break;
                }

                case 'Pop':
                    await playPopSound(sounds);
                    await delay(120, currentSpeedMultiplier);
                    break;

                case 'Happy Detected': {
                    const detected = await waitForExpressionDetection('happy', currentSpeedMultiplier);
                    if (detected) {
                        console.log('?? Happy face detected!');
                    } else {
                        console.warn('?? Happy expression not detected within timeout');
                    }
                    break;
                }

                case 'Emotion Detected':
                case 'emotionDetected': {
                    const expressionToMatch = b.expression || 'happy';
                    const detected = await waitForExpressionDetection(expressionToMatch, currentSpeedMultiplier);
                    if (detected) {
                        console.log(`?? Expression detected: ${expressionToMatch}`);
                    } else {
                        console.warn(`?? Expression "${expressionToMatch}" not detected within timeout`);
                    }
                    break;
                }

                case 'Color Detected':
                case 'colorDetected': {
                    const targetColor = b?.targetColor || b?.color || '#FF0000';
                    const detected = await waitForColorDetection(targetColor, currentSpeedMultiplier);
                    if (detected) {
                        console.log(`?? Color detected: ${targetColor}`);
                    } else {
                        console.warn(`?? Color "${targetColor}" not detected (camera may be off)`);
                    }
                    break;
                }

                case "Move With Hand Detection": {
                    console.log("Move With Hand Detection block running for actor", actor.id);
                    const dir = window.handDetectionData?.direction;
                    if (!dir) {
                        await delay(100);
                        break;
                    }
                    let dx = 0, dy = 0;
                    if (dir === "up") dy = -1;
                    if (dir === "down") dy = 1;
                    if (dir === "left") dx = -1;
                    if (dir === "right") dx = 1;
                    dispatch(moveActor({ actorId: actor.id, dx, dy, fromScript: true }));
                    await delay(100);
                    break;
                }

                case "Sync Actors with Faces":
                case "sync_actors_with_faces": {
                    const detectedFacesRaw = window?.humanDetectionData?.faceCount;
                    const faceCount = Math.max(0, Math.min(20, Number.isFinite(detectedFacesRaw) ? detectedFacesRaw : 0));
                    console.log(`?? Sync Actors with Faces block - detected faces: ${faceCount}`);
                    dispatch(syncActorsWithFaces(faceCount));
                    await waitForReduxUpdate();
                    break;
                }

                case "Go To Face Part":
                case "facePartAction": {
                    const partName = b.part || 'nose';

                    const clampGrid = (val, max) => Math.max(0, Math.min(max, val));
                    const maxX = 37;
                    const maxY = 22;

                    const smoothStore = (window.__facePartSmooth = window.__facePartSmooth || {});
                    const history = (smoothStore[actor.id] = smoothStore[actor.id] || []);

                    while (!shouldStop) {
                        await yieldIfRecording();
                        const part = getFacePart(partName);
                        const vw = window.humanDetectionData?.videoWidth || 640;
                        const vh = window.humanDetectionData?.videoHeight || 360;

                        if (!part || !vw || !vh) {
                            await delay(40, currentSpeedMultiplier);
                            continue;
                        }

                        const targetX = clampGrid((part.x / vw) * 38, maxX);
                        const targetY = clampGrid((part.y / vh) * 23, maxY);

                        history.push({ x: targetX, y: targetY });
                        if (history.length > 5) history.shift();

                        const median = (arr, key) => {
                            const vals = arr.map(p => p[key]).sort((a, b) => a - b);
                            const mid = Math.floor(vals.length / 2);
                            return vals.length % 2 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2;
                        };
                        const smoothX = median(history, 'x');
                        const smoothY = median(history, 'y');

                        const currentState = getSceneState(dispatch);
                        const actorState = currentState?.scene?.scenes?.[currentState?.scene?.currentSceneIndex]?.actors?.find(a => a.id === actor.id);
                        const prevX = actorState?.x ?? actor.x;
                        const prevY = actorState?.y ?? actor.y;
                        const deltaX = smoothX - prevX;
                        const deltaY = smoothY - prevY;
                        const jitterThreshold = 0.35;

                        if (Math.abs(deltaX) > jitterThreshold || Math.abs(deltaY) > jitterThreshold) {
                            dispatch(setActorPosition({
                                actorId: actor.id,
                                x: smoothX,
                                y: smoothY,
                                fromScript: true
                            }));
                        }

                        await delay(50, currentSpeedMultiplier);
                    }
                    break;
                }

                case "Go To Hand Part":
                case "handPartAction": {
                    const fingerName = normalizeFinger(b.finger || 'index finger');

                    const clampGrid = (val, max) => Math.max(0, Math.min(max, val));
                    const maxX = 37;
                    const maxY = 22;

                    const smoothStore = (window.__handPartSmooth = window.__handPartSmooth || {});
                    const history = (smoothStore[actor.id] = smoothStore[actor.id] || []);

                    while (!shouldStop) {
                        await yieldIfRecording();
                        const joint = getFingerJointPosition(fingerName);  // ? FIXED
                        const vw = window.humanDetectionData?.videoWidth || 640;
                        const vh = window.humanDetectionData?.videoHeight || 360;

                        if (!joint || !vw || !vh) {  // ? CHANGE tip TO joint
                            await delay(40, currentSpeedMultiplier);
                            continue;
                        }

                        const targetX = clampGrid((joint.x / vw) * 38, maxX);  // ? CHANGE tip TO joint
                        const targetY = clampGrid((joint.y / vh) * 23, maxY);  // ? CHANGE tip TO joint

                        history.push({ x: targetX, y: targetY });
                        if (history.length > 5) history.shift();

                        const median = (arr, key) => {
                            const vals = arr.map(p => p[key]).sort((a, b) => a - b);
                            const mid = Math.floor(vals.length / 2);
                            return vals.length % 2 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2;
                        };
                        const smoothX = median(history, 'x');
                        const smoothY = median(history, 'y');

                        const currentState = getSceneState(dispatch);
                        const actorState = currentState?.scene?.scenes?.[currentState?.scene?.currentSceneIndex]?.actors?.find(a => a.id === actor.id);
                        const prevX = actorState?.x ?? actor.x;
                        const prevY = actorState?.y ?? actor.y;
                        const deltaX = smoothX - prevX;
                        const deltaY = smoothY - prevY;
                        const jitterThreshold = 0.35;

                        if (Math.abs(deltaX) > jitterThreshold || Math.abs(deltaY) > jitterThreshold) {
                            dispatch(setActorPosition({
                                actorId: actor.id,
                                x: smoothX,
                                y: smoothY,
                                fromScript: true
                            }));
                        }

                        await delay(50, currentSpeedMultiplier);
                    }
                    break;
                }

                default:
                    console.warn(`?? Unknown block type: ${blockIdentifier}`);
                    break;
            }

            await yieldIfRecording();
            await delay(80, currentSpeedMultiplier);
        }
    }
    // ??? END OF RECURSIVE FUNCTION ???
    try {
        let repeatForever = actor.scripts.some(b => b?.type === "repeatBlock");

        // ? SIMPLIFIED MAIN LOOP - CALLS RECURSIVE FUNCTION
        do {
            for (const stack of stacks) {
                if (shouldStop) break;
                await executeScript(stack);
            }

            if (!repeatForever || shouldStop) break;

        } while (true);

        console.log('? Script execution completed');

    } catch (error) {
        if (error.message === 'STOP_EXECUTION') {
            console.log('?? Script stopped by Stop block');
            resetRobotServos();
        } else {
            console.error('? Script execution error:', error);
            throw error;
        }
    } finally {
        stopPalmControl();
        releaseStartRun();
        if (qrScanActivatedByRun) {
            activeQrScanRuns.delete(runId);
            if (!activeQrScanRuns.size) {
                dispatch(setQrScanEnabled(false));
            }
        }
        if (numberDetectActivatedByRun) {
            activeNumberDetectRuns.delete(runId);
        }
    }

}

// [All other export functions remain the same]
export function initializeDrawLineCanvas() {
    console.log('?? Draw line canvas initialized');
}

export function clearDrawnPaths() {
    localStorage.removeItem('simulatorDrawnPaths');
    window.dispatchEvent(new CustomEvent('clearDrawnPaths'));
    console.log('?? All drawn paths cleared');
}

export function getDrawLineSettings() {
    try {
        const stored = localStorage.getItem('simulatorDrawLineSettings');
        return stored ? JSON.parse(stored) : {
            enabled: false,
            thickness: 3,
            size: 10,
            color: '#FF0000'
        };
    } catch (error) {
        console.error('Error getting draw line settings:', error);
        return {
            enabled: false,
            thickness: 3,
            size: 10,
            color: '#FF0000'
        };
    }
}

export function setDrawLineSettings(settings) {
    try {
        localStorage.setItem('simulatorDrawLineSettings', JSON.stringify(settings));
        window.dispatchEvent(new CustomEvent('drawLineSettingsChanged', {
            detail: settings
        }));
        console.log('?? Draw line settings updated:', settings);
        return true;
    } catch (error) {
        console.error('Error setting draw line settings:', error);
        return false;
    }
}

export function addObstacleToScene(obstacleData, dispatch) {
    try {
        console.log('Adding obstacle from simulator:', obstacleData);
        dispatch(addObstacle(obstacleData));
        console.log('? Obstacle added successfully');
        return true;
    } catch (error) {
        console.error('? Error adding obstacle:', error);
        return false;
    }
}

export function removeObstacleFromScene(obstacleId, dispatch) {
    try {
        console.log('Removing obstacle from simulator:', obstacleId);
        dispatch(removeObstacle(obstacleId));
        console.log('? Obstacle removed successfully');
        return true;
    } catch (error) {
        console.error('? Error removing obstacle:', error);
        return false;
    }
}

export function moveObstacleInScene(obstacleId, newX, newY, dispatch) {
    try {
        console.log('Moving obstacle in simulator:', obstacleId, newX, newY);
        dispatch(moveObstacle({ id: obstacleId, x: newX, y: newY }));
        console.log('? Obstacle moved successfully');
        return true;
    } catch (error) {
        console.error('? Error moving obstacle:', error);
        return false;
    }
}

export function addColoredAreaToScene(coloredAreaData, dispatch) {
    try {
        console.log('Adding colored area from simulator:', coloredAreaData);
        dispatch(addColoredArea(coloredAreaData));
        console.log('? Colored area added successfully');
        return true;
    } catch (error) {
        console.error('? Error adding colored area:', error);
        return false;
    }
}

export function removeColoredAreaFromScene(coloredAreaId, dispatch) {
    try {
        console.log('Removing colored area from simulator:', coloredAreaId);
        dispatch(removeColoredArea(coloredAreaId));
        console.log('? Colored area removed successfully');
        return true;
    } catch (error) {
        console.error('? Error removing colored area:', error);
        return false;
    }
}

export function moveColoredAreaInScene(coloredAreaId, newX, newY, dispatch) {
    try {
        console.log('Moving colored area in simulator:', coloredAreaId, newX, newY);
        dispatch(moveColoredArea({ id: coloredAreaId, x: newX, y: newY }));
        console.log('? Colored area moved successfully');
        return true;
    } catch (error) {
        console.error('? Error moving colored area:', error);
        return false;
    }
}

export function addSimulatorRobotToScene(robotData, dispatch) {
    try {
        console.log('Adding robot to simulator:', robotData);
        dispatch(addSimulatorRobot(robotData));
        console.log('? Robot added to simulator successfully');
        return true;
    } catch (error) {
        console.error('? Error adding robot to simulator:', error);
        return false;
    }
}

export function removeSimulatorRobotFromScene(robotId, dispatch) {
    try {
        console.log('Removing robot from simulator:', robotId);
        dispatch(removeSimulatorRobot(robotId));
        console.log('? Robot removed from simulator successfully');
        return true;
    } catch (error) {
        console.error('? Error removing robot from simulator:', error);
        return false;
    }
}

export function moveSimulatorRobotInScene(robotId, newX, newY, dispatch) {
    try {
        console.log('Moving robot in simulator:', robotId, newX, newY);
        dispatch(moveSimulatorRobot({ id: robotId, x: newX, y: newY }));
        console.log('? Robot moved in simulator successfully');
        return true;
    } catch (error) {
        console.error('? Error moving robot in simulator:', error);
        return false;
    }
}

export function cycleSimulatorRobotType(dispatch) {
    try {
        console.log('Cycling to next robot type in simulator');
        dispatch(cycleSimulatorRobot());
        console.log('? Robot cycled successfully');
        return true;
    } catch (error) {
        console.error('? Error cycling robot type:', error);
        return false;
    }
}

export async function uploadSimulatorBackground() {
    return new Promise((resolve) => {
        try {
            console.log('Opening file picker for simulator background upload...');

            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.multiple = false;

            input.onchange = (event) => {
                const file = event.target.files[0];
                if (!file) {
                    console.log("No file selected");
                    resolve(false);
                    return;
                }

                if (!file.type.startsWith('image/')) {
                    console.error('Invalid file type. Please select an image file.');
                    alert('Please select an image file (PNG, JPG, GIF, etc.)');
                    resolve(false);
                    return;
                }

                const maxSize = 10 * 1024 * 1024;
                if (file.size > maxSize) {
                    console.error('File too large. Maximum size is 10MB.');
                    alert('File too large. Please select an image smaller than 10MB.');
                    resolve(false);
                    return;
                }

                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const imageDataURL = e.target.result;

                        const customBackgrounds = getCustomSimulatorBackgrounds();
                        const newCustomBg = {
                            id: `custom_${Date.now()}`,
                            name: file.name,
                            dataURL: imageDataURL,
                            uploadedAt: new Date().toISOString()
                        };

                        customBackgrounds.push(newCustomBg);

                        if (customBackgrounds.length > 5) {
                            customBackgrounds.shift();
                        }

                        localStorage.setItem('simulatorCustomBackgrounds', JSON.stringify(customBackgrounds));
                        localStorage.setItem('simulatorBackground', imageDataURL);

                        window.dispatchEvent(new CustomEvent('simulatorBackgroundChanged'));

                        console.log('? Custom background uploaded and set:', file.name);
                        resolve(true);
                    } catch (error) {
                        console.error('Error processing image:', error);
                        alert('Error processing image. Please try a different file.');
                        resolve(false);
                    }
                };

                reader.onerror = () => {
                    console.error('Error reading file');
                    alert('Error reading file. Please try again.');
                    resolve(false);
                };

                reader.readAsDataURL(file);
            };

            input.click();
        } catch (error) {
            console.error('Error opening file picker:', error);
            resolve(false);
        }
    });
}

export function getCustomSimulatorBackgrounds() {
    try {
        const stored = localStorage.getItem('simulatorCustomBackgrounds');
        return stored ? JSON.parse(stored) : [];
    } catch (error) {
        console.error('Error getting custom backgrounds:', error);
        return [];
    }
}

export function getAllSimulatorBackgrounds() {
    const builtInBackgrounds = [
        './assets/backgrounds/bg1.svg',
        './assets/backgrounds/bg2.svg',
        './assets/backgrounds/bg3.svg',
        './assets/backgrounds/bg4.svg',
        './assets/backgrounds/bg5.svg'
    ];

    const customBackgrounds = getCustomSimulatorBackgrounds();
    const customDataURLs = customBackgrounds.map(bg => bg.dataURL);

    return [...builtInBackgrounds, ...customDataURLs];
}

export function cycleSimulatorBackground() {
    const allBackgrounds = getAllSimulatorBackgrounds();

    const currentBg = localStorage.getItem('simulatorBackground') || allBackgrounds[0];

    let currentIndex = allBackgrounds.findIndex(bg => bg === currentBg);
    if (currentIndex === -1) currentIndex = -1;

    const nextIndex = (currentIndex + 1) % allBackgrounds.length;
    const nextBackground = allBackgrounds[nextIndex];

    localStorage.setItem('simulatorBackground', nextBackground);

    window.dispatchEvent(new CustomEvent('simulatorBackgroundChanged'));

    console.log('?? Cycling simulator background to:', nextBackground.substring(0, 50) + '...');
    return nextBackground;
}

export function getCurrentSimulatorBackground() {
    const stored = localStorage.getItem('simulatorBackground');
    if (stored) {
        return stored;
    }
    return './assets/backgrounds/bg1.svg';
}

export function clearCustomSimulatorBackgrounds() {
    try {
        localStorage.removeItem('simulatorCustomBackgrounds');

        const currentBg = getCurrentSimulatorBackground();
        if (currentBg.startsWith('data:')) {
            localStorage.setItem('simulatorBackground', './assets/backgrounds/bg1.svg');
            window.dispatchEvent(new CustomEvent('simulatorBackgroundChanged'));
        }

        console.log('? All custom simulator backgrounds cleared');
        return true;
    } catch (error) {
        console.error('? Error clearing custom backgrounds:', error);
        return false;
    }
}

export function setSceneBackground(backgroundUrl, dispatch) {
    try {
        console.log('Setting background from simulator:', backgroundUrl);
        dispatch(setBackground(backgroundUrl));
        console.log('? Background set successfully');
        return true;
    } catch (error) {
        console.error('? Error setting background:', error);
        return false;
    }
}

export async function saveProjectFromSimulator(dispatch, getState) {
    try {
        console.log('Saving project from simulator...');
        const state = getState();
        const projectData = {
            scenes: state.scene.scenes,
            currentSceneIndex: state.scene.currentSceneIndex,
            backgroundGallery: state.scene.backgroundGallery,
            customSounds: state.scene.customSounds,
            simulatorRobots: state.scene.simulatorRobots,
            savedAt: new Date().toISOString()
        };

        const dataStr = JSON.stringify(projectData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(dataBlob);
        link.download = `stembot-project-${new Date().getTime()}.json`;
        link.click();

        console.log('? Project saved successfully');
        return true;
    } catch (error) {
        console.error('? Error saving project:', error);
        return false;
    }
}

export async function loadProjectFromSimulator(dispatch) {
    return new Promise((resolve) => {
        try {
            console.log('Loading project from simulator...');

            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.json';

            input.onchange = (event) => {
                const file = event.target.files[0];
                if (!file) {
                    resolve(false);
                    return;
                }

                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const projectData = JSON.parse(e.target.result);
                        dispatch(overwrite(projectData));
                        console.log('? Project loaded successfully');
                        resolve(true);
                    } catch (error) {
                        console.error('? Error parsing project file:', error);
                        resolve(false);
                    }
                };

                reader.readAsText(file);
            };

            input.click();
        } catch (error) {
            console.error('? Error loading project:', error);
            resolve(false);
        }
    });
}

// ??? FINAL: Export pathFollower for external access
// export { pathFollower };

// // ??? CRITICAL: Last check to ensure global availability
// console.log('??? Final global check - window.pathFollower available:', !!window.pathFollower);
// console.log('?? COMPLETE DYNAMIC PATH FOLLOWING SYSTEM READY! ????');

// ========== Open Roberta JSON export/import helpers (non-breaking) ==========
// Local grid constants (kept consistent with sceneSlice defaults)
const __ORL_RS_GRID_W__ = 20;
const __ORL_RS_GRID_H__ = 15;

const __RS_toUnit__ = (v, max) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(1, n / max));
};
const __RS_fromUnit__ = (v, max) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return Math.round(Math.max(0, Math.min(1, n)) * max);
};

/**
* Export current Redux scene state to Open Roberta-style config.
* This is pure and does not dispatch or change any behavior.
*/
export function exportOpenRobertaSimConfig(getState) {
    try {
        const state = getState();
        const sceneState = state?.scene;
        const currentScene = sceneState?.scenes?.[sceneState?.currentSceneIndex] || {};
        const robots = sceneState?.simulatorRobots || [];

        const robotPosesInner = robots.map(r => ({
            x: __RS_toUnit__(r?.x ?? 0, __ORL_RS_GRID_W__),
            y: __RS_toUnit__(r?.y ?? 0, __ORL_RS_GRID_H__),
            theta: Number.isFinite(r?.direction) ? r.direction : 0
        }));

        const obstacles = (currentScene?.obstacles || []).map(obs => {
            const wCells = Number.isFinite(obs?.w) ? obs.w
                : (Number.isFinite(obs?.width) ? obs.width : 1);
            const hCells = Number.isFinite(obs?.h) ? obs.h
                : (Number.isFinite(obs?.height) ? obs.height : 1);
            return {
                x: __RS_toUnit__(obs?.x ?? 0, __ORL_RS_GRID_W__),
                y: __RS_toUnit__(obs?.y ?? 0, __ORL_RS_GRID_H__),
                w: __RS_toUnit__(wCells, __ORL_RS_GRID_W__),
                h: __RS_toUnit__(hCells, __ORL_RS_GRID_H__),
                theta: Number.isFinite(obs?.theta) ? obs.theta : (Number.isFinite(obs?.rotation) ? obs.rotation : 0),
                color: obs?.color || '#33B8CA',
                form: obs?.form || obs?.shape || 'RECTANGLE',
                type: obs?.type || 'OBSTACLE'
            };
        });

        const colorAreas = (currentScene?.coloredAreas || []).map(area => {
            const wCells = Number.isFinite(area?.w) ? area.w
                : (Number.isFinite(area?.width) ? area.width : 1);
            const hCells = Number.isFinite(area?.h) ? area.h
                : (Number.isFinite(area?.height) ? area.height : 1);
            return {
                x: __RS_toUnit__(area?.x ?? 0, __ORL_RS_GRID_W__),
                y: __RS_toUnit__(area?.y ?? 0, __ORL_RS_GRID_H__),
                w: __RS_toUnit__(wCells, __ORL_RS_GRID_W__),
                h: __RS_toUnit__(hCells, __ORL_RS_GRID_H__),
                color: area?.color || '#000000',
                type: area?.type || 'COLOR_AREA'
            };
        });

        return {
            robotPoses: [robotPosesInner],
            obstacles,
            colorAreas,
            marker: []
        };
    } catch (e) {
        console.error('exportOpenRobertaSimConfig error:', e);
        return { robotPoses: [[]], obstacles: [], colorAreas: [], marker: [] };
    }
}

/**
* Import an Open Roberta-style config into the current scene using existing actions.
* No UI is changed and no existing behavior is replaced; this only dispatches your current reducers.
*/
export function importOpenRobertaSimConfig(config, dispatch, getState) {
    try {
        if (!config || typeof config !== 'object') return;

        const state = getState();
        const sceneState = state?.scene;
        if (!sceneState) return;

        const gridW = __ORL_RS_GRID_W__;
        const gridH = __ORL_RS_GRID_H__;

        // Obstacles
        const obs = Array.isArray(config.obstacles) ? config.obstacles : [];
        for (const o of obs) {
            const cellX = __RS_fromUnit__(o?.x ?? 0, gridW);
            const cellY = __RS_fromUnit__(o?.y ?? 0, gridH);
            const cellW = Math.max(1, __RS_fromUnit__(o?.w ?? 0, gridW));
            const cellH = Math.max(1, __RS_fromUnit__(o?.h ?? 0, gridH));

            dispatch(addObstacle({
                id: `orl-${Date.now()}-${Math.random()}`,
                x: cellX,
                y: cellY,
                w: cellW,
                h: cellH,
                theta: Number.isFinite(o?.theta) ? o.theta : 0,
                rotation: Number.isFinite(o?.theta) ? o.theta : 0,
                color: o?.color || '#33B8CA',
                shape: o?.form || 'RECTANGLE',
                form: o?.form || 'RECTANGLE',
                type: o?.type || 'OBSTACLE'
            }));
        }

        // Color areas
        const areas = Array.isArray(config.colorAreas) ? config.colorAreas : [];
        for (const a of areas) {
            const cellX = __RS_fromUnit__(a?.x ?? 0, gridW);
            const cellY = __RS_fromUnit__(a?.y ?? 0, gridH);
            const cellW = Math.max(1, __RS_fromUnit__(a?.w ?? 0, gridW));
            const cellH = Math.max(1, __RS_fromUnit__(a?.h ?? 0, gridH));

            dispatch(addColoredArea({
                id: `orl-color-${Date.now()}-${Math.random()}`,
                x: cellX,
                y: cellY,
                width: cellW,
                height: cellH,
                color: a?.color || '#000000',
                type: 'coloredArea',
                blocking: false
            }));
        }

        // Robot pose (first one)
        const firstPose = Array.isArray(config.robotPoses)
            && Array.isArray(config.robotPoses[0])
            && config.robotPoses[0][0]
            ? config.robotPoses[0][0]
            : null;

        if (firstPose) {
            const posX = __RS_fromUnit__(firstPose.x ?? 0, gridW);
            const posY = __RS_fromUnit__(firstPose.y ?? 0, gridH);
            const theta = Number.isFinite(firstPose.theta) ? firstPose.theta : 0;

            const currentRobots = sceneState.simulatorRobots || [];
            if (currentRobots.length === 0) {
                dispatch(addSimulatorRobot({
                    name: 'Robot',
                    x: posX,
                    y: posY,
                    direction: theta,
                    image: './assets/characters/stembot.svg'
                }));
            } else {
                dispatch(moveSimulatorRobot({
                    id: currentRobots[0].id,
                    x: posX,
                    y: posY,
                    direction: theta
                }));
            }
        }
    } catch (e) {
        console.error('importOpenRobertaSimConfig error:', e);
    }
}

/**
* Optional helper to download the exported config as a file named NEPOprog-sim_configuration.json
*/
export function downloadOpenRobertaSimConfig(getState) {
    try {
        const cfg = exportOpenRobertaSimConfig(getState);
        const blob = new Blob([JSON.stringify(cfg, null, 2)], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'NEPOprog-sim_configuration.json';
        a.click();
    } catch (e) {
        console.error('downloadOpenRobertaSimConfig error:', e);
    }
}

